import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import toast from "react-hot-toast";
import ReactAudioPlayer from "react-audio-player";
import { useSelector } from "react-redux";
import { cn } from "@/lib/utils";
import {
    MessageSquare,
    Send,
    Paperclip,
    Download,
    Loader2,
    Sparkles,
    Image as ImageIcon,
    Video,
    FileText,
    Music,
    MoreVertical,
    Clock,
    FileUp,
    Brush,
    Circle,
    X,
    ArrowUp,
    Reply,
    Pencil,
    Trash2,
    Eye,
    EyeOff
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetFooter,
} from "@/components/ui/sheet";

import AudioRecorder from "./audioRecorder";
import VideoRecorder from "./videoRecorder";
import ScreenRecorder from "./screenRecorder";
import ScreenshotCapture from "./screenshotCapture";
import MarkdownText from "./MarkdownText";
import { downloadFile } from "../utils/utils";
import { BACKEND_API_URL } from "../utils/api";
import { filterMentionSuggestions } from "@/utils/mentionUtils";
import useCanvasStore from "../stores/canvasStore";
import * as nodeEmoji from 'node-emoji';

// Production media URL
const PRODUCTION_MEDIA_URL = 'https://onemap.morphlelabs.com';

// Detect if a line looks like ASCII art or a diagram that needs space preservation
// Returns true if the line contains patterns typical of ASCII art
const isAsciiArtLine = (line) => {
    // Check for vertical line characters (common in diagrams) - any line with | is likely ASCII art
    if (/\|/.test(line)) return true;

    // Check for horizontal lines made of repeated characters (=== or --- with 4+ chars)
    if (/[=]{4,}|[-]{4,}/.test(line)) return true;

    // Check for box-drawing patterns like [Text] with significant spacing between brackets
    if (/\[.+\].*  .*\[.+\]/.test(line)) return true;

    // Check for tree/hierarchy drawing characters
    if (/[├└┌┐│─┬┴┼]/.test(line)) return true;

    // Check for lines that are mostly whitespace with bracketed text (diagram headers/nodes)
    if (/^\s*\[.+\]\s*$/.test(line) && /  /.test(line)) return true;

    return false;
};

// Comprehensive emoji conversion function that handles all Unicode emojis
// First tries nodeEmoji.unemojify, then encodes any remaining emojis as [U+XXXX] format
// This preserves all emojis while preventing database encoding errors
// Preserves newlines and other whitespace
const convertEmojisToShortcodes = (text) => {
    if (!text || typeof text !== 'string') return text || '';

    try {
        // First, try nodeEmoji.unemojify for known emojis (converts to :shortcode: format)
        let converted = nodeEmoji.unemojify(text);

        // Comprehensive emoji regex covering all Unicode emoji ranges
        // Includes: Miscellaneous Symbols (2600-26FF), Geometric Shapes Extended (1F780-1F7FF), etc.
        const emojiRegex = /[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F600}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA00}-\u{1FAFF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{1F780}-\u{1F7FF}]|[\u{1FA70}-\u{1FAFF}]/gu;

        // Instead of removing unrecognized emojis, encode them as [U+XXXX] format
        // This preserves the emoji information while making it database-safe
        converted = converted.replace(emojiRegex, (match) => {
            // Get the Unicode code point(s) for the emoji
            // Handle multi-codepoint emojis (like flags or skin tone modifiers)
            let codePoints = [];
            for (let i = 0; i < match.length; i++) {
                const codePoint = match.codePointAt(i);
                if (codePoint >= 0x1F300) { // Only encode actual emoji ranges
                    codePoints.push(codePoint.toString(16).toUpperCase().padStart(4, '0'));
                    // Skip the next character if it's a surrogate pair
                    if (codePoint > 0xFFFF) i++;
                } else {
                    // Non-emoji character, keep as-is
                    return match[i];
                }
            }
            // Encode as [U+XXXX] format for single emoji or [U+XXXX][U+YYYY] for multi-codepoint
            return codePoints.map(cp => `[U+${cp}]`).join('');
        });

        // Clean up multiple spaces but preserve ASCII art, list indentation, and code blocks
        const lines = converted.split('\n');
        const processedLines = lines.map(line => {
            // ASCII art detection - preserve all spaces for diagram lines
            if (isAsciiArtLine(line)) {
                return line; // Preserve exactly as-is
            }

            // Check if line starts with list marker (preserve indentation)
            const listMatch = line.match(/^(\s*)([-*+]|\d+\.)\s/);
            if (listMatch) {
                // This is a list item - preserve leading indentation, only collapse spaces after the marker
                const [, indent, marker] = listMatch;
                const content = line.substring(listMatch[0].length);
                // Collapse multiple spaces in content only
                const cleanedContent = content.replace(/[ \t]{2,}/g, ' ');
                return indent + marker + ' ' + cleanedContent;
            }
            // Check if line is indented code or preserves structure
            if (/^\s{2,}/.test(line)) {
                // Heavily indented line - might be code or nested content, preserve indentation
                const match = line.match(/^(\s+)(.*)$/);
                if (match) {
                    const [, indent, content] = match;
                    // Only collapse spaces in content, preserve leading indent
                    const cleanedContent = content.replace(/[ \t]{2,}/g, ' ');
                    return indent + cleanedContent;
                }
            }
            // Regular line - collapse multiple spaces/tabs
            return line.replace(/[ \t]+/g, ' ');
        });

        return processedLines.join('\n');
    } catch (error) {
        console.warn('Error converting emojis:', error);
        // If conversion fails, try to encode emojis directly as [U+XXXX] format
        try {
            const emojiRegex = /[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F600}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA00}-\u{1FAFF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{1F780}-\u{1F7FF}]|[\u{1FA70}-\u{1FAFF}]/gu;
            const encoded = text.replace(emojiRegex, (match) => {
                let codePoints = [];
                for (let i = 0; i < match.length; i++) {
                    const codePoint = match.codePointAt(i);
                    if (codePoint >= 0x1F300) {
                        codePoints.push(codePoint.toString(16).toUpperCase().padStart(4, '0'));
                        if (codePoint > 0xFFFF) i++;
                    } else {
                        return match[i];
                    }
                }
                return codePoints.map(cp => `[U+${cp}]`).join('');
            });
            // Preserve ASCII art and list indentation in fallback encoding too
            const lines = encoded.split('\n');
            const processedLines = lines.map(line => {
                // ASCII art detection - preserve all spaces for diagram lines
                if (isAsciiArtLine(line)) {
                    return line;
                }
                const listMatch = line.match(/^(\s*)([-*+]|\d+\.)\s/);
                if (listMatch) {
                    const [, indent, marker] = listMatch;
                    const content = line.substring(listMatch[0].length);
                    const cleanedContent = content.replace(/[ \t]{2,}/g, ' ');
                    return indent + marker + ' ' + cleanedContent;
                }
                if (/^\s{2,}/.test(line)) {
                    const match = line.match(/^(\s+)(.*)$/);
                    if (match) {
                        const [, indent, content] = match;
                        const cleanedContent = content.replace(/[ \t]{2,}/g, ' ');
                        return indent + cleanedContent;
                    }
                }
                return line.replace(/[ \t]+/g, ' ');
            });
            return processedLines.join('\n');
        } catch (e) {
            console.error('Failed to encode emojis:', e);
            // Last resort: remove emojis to prevent database errors
            const emojiRegex = /[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F600}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA00}-\u{1FAFF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{1F780}-\u{1F7FF}]|[\u{1FA70}-\u{1FAFF}]/gu;
            return text.replace(emojiRegex, '').replace(/[ \t]+/g, '') || '';
        }
    }
};

// Map text emoticons to Unicode emojis (same as MessageCard)
const emoticonToEmoji = {
    ':)': '😊',
    ':-)': '😊',
    ':(': '😢',
    ':-(': '😢',
    ':D': '😃',
    ':-D': '😃',
    '<3': '❤️',
    ':P': '😛',
    ':-P': '😛',
    ';)': '😉',
    ';-)': '😉',
    ':O': '😮',
    ':-O': '😮',
    ':|': '😐',
    ':-|': '😐',
    ':/': '😕',
    ':-/': '😕',
    ':\\': '😕',
    ':-\\': '😕',
    ':*': '😘',
    ':-*': '😘',
    '>:(': '😠',
    '>:-(': '😠',
    ':\'(': '😭',
    ':\'-(': '😭',
    'XD': '😆',
    'xD': '😆',
    'xP': '😝',
    'XP': '😝',
};

// Convert text emoticons and shortcodes to actual emojis for display
// Also decodes [U+XXXX] sequences back to emojis
const convertToEmojis = (text) => {
    if (!text) return text;
    let result = text;

    // First, decode [U+XXXX] sequences back to emojis (for unrecognized emojis)
    // This handles emojis that were encoded because they don't have shortcodes
    // Handle multiple consecutive [U+XXXX] sequences for multi-codepoint emojis
    result = result.replace(/\[U\+([0-9A-F]{4,})\](?:\[U\+([0-9A-F]{4,})\])*/gi, (match) => {
        try {
            // Extract all code points from the match
            const codePoints = [];
            const regex = /\[U\+([0-9A-F]{4,})\]/gi;
            let m;
            while ((m = regex.exec(match)) !== null) {
                codePoints.push(parseInt(m[1], 16));
            }
            // Combine all code points into a single emoji (for multi-codepoint emojis like flags)
            return String.fromCodePoint(...codePoints);
        } catch (e) {
            console.warn('Failed to decode emoji codes:', match, e);
            return match; // Return original if decoding fails
        }
    });

    // Then, convert text emoticons to emojis (e.g., :) → 😊)
    // Sort by length descending to match longer emoticons first (e.g., :-) before :))
    const sortedEmoticons = Object.entries(emoticonToEmoji).sort((a, b) => b[0].length - a[0].length);
    for (const [emoticon, emoji] of sortedEmoticons) {
        // Use word boundary-like matching to avoid partial replacements
        const escaped = emoticon.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`(^|\\s)${escaped}(\\s|$)`, 'g');
        result = result.replace(regex, `$1${emoji}$2`);
    }

    // Finally, convert shortcodes to emojis (e.g., :rocket: → 🚀)
    result = nodeEmoji.emojify(result);

    return result;
};

/**
 * Focus Mode Comments Panel - Enhanced chat-style UI for viewing item comments
 * Features:
 * - Better scroll handling with auto-scroll to bottom
 * - Improved media previews
 * - Better loading and empty states
 * - Smooth animations
 * - Better visual hierarchy
 * - Multi-select comments and promote to folders
 */
const FocusModeCommentsPanel = ({ itemId, itemType, onPromoteComplete }) => {
    const location = useLocation();
    const { user, user_settings } = useSelector((state) => state.auth);
    const isMinimalView = user_settings?.minimal_mode;
    const { focusModeMessagesHidden, setFocusModeMessagesHidden, unreadCounts, markAllRead } = useCanvasStore();

    // Get unread count for current item (hidden in minimal view)
    const ownUnreadCount = isMinimalView ? 0 : (unreadCounts[itemId]?.own_unread_count || 0);
    const [itemInfo, setItemInfo] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [comment, setComment] = useState("");
    const [savingComment, setSavingComment] = useState(false);
    const scrollRef = useRef(null);
    const prevItemIdRef = useRef(null);
    const previousCommentCountRef = useRef(0);
    const isInitialLoadRef = useRef(true);
    const lastCommentIdRef = useRef(null);
    const fileInputRef = useRef(null);
    const generalFileInputRef = useRef(null);
    const clearRecordingRef = useRef(null);
    const clearVideoRecordingRef = useRef(null);
    const clearScreenRecordingRef = useRef(null);
    const mobileScreen = typeof window !== 'undefined' ? window.innerWidth <= 600 : false;
    const [selectedComments, setSelectedComments] = useState([]);
    const [focusedCommentIndex, setFocusedCommentIndex] = useState(-1);
    const commentRefs = useRef({});
    const lastSelectedIndexRef = useRef(null);
    const [isPromoting, setIsPromoting] = useState(false);
    const [selectedReplyComment, setSelectedReplyComment] = useState(null);
    const inputRef = useRef(null);

    // Highlighted comment from URL (for search result navigation)
    const [highlightedCommentId, setHighlightedCommentId] = useState(null);

    // Edit state
    const [editingCommentId, setEditingCommentId] = useState(null);
    const [editedComment, setEditedComment] = useState("");
    const [isUpdating, setIsUpdating] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [commentToDelete, setCommentToDelete] = useState(null);
    const editInputRef = useRef(null);

    // Mention autocomplete state for edit mode
    const [editMentionQuery, setEditMentionQuery] = useState('');
    const [editMentionSuggestions, setEditMentionSuggestions] = useState([]);
    const [showEditMentionSuggestions, setShowEditMentionSuggestions] = useState(false);
    const [editMentionStartIndex, setEditMentionStartIndex] = useState(-1);
    const [selectedEditMentionIndex, setSelectedEditMentionIndex] = useState(0);

    // Mention autocomplete state
    const [mentionQuery, setMentionQuery] = useState('');
    const [mentionSuggestions, setMentionSuggestions] = useState([]);
    const [showMentionSuggestions, setShowMentionSuggestions] = useState(false);
    const [mentionStartIndex, setMentionStartIndex] = useState(-1);
    const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
    const mentionSuggestionsRef = useRef(null);

    // Media preview and annotation state
    const [mediaFile, setMediaFile] = useState(null);
    const [mediaType, setMediaType] = useState('');
    const [isMediaSelected, setIsMediaSelected] = useState(false);
    const [showRecordingDialog, setShowRecordingDialog] = useState(false);
    const [showAnnotationDialog, setShowAnnotationDialog] = useState(false);
    const [annotationImage, setAnnotationImage] = useState(null);
    const annotationCanvasRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [brushSize, setBrushSize] = useState('10');
    const [screenshotCanvas, setScreenshotCanvas] = useState(null);
    const [screenshotQuality, setScreenshotQuality] = useState('0.75');
    const imageLoadedRef = useRef(false);
    const videoFileInputRef = useRef(null);
    const videoRef = useRef(null);

    // Pagination state
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    // Get comments from item info
    const comments = useMemo(() => {
        return itemType === 'folder'
            ? (itemInfo?.folder_comments || [])
            : (itemInfo?.link_comments || []);
    }, [itemInfo, itemType]);

    // Filter comments to show only user comments (is_comment=true) and show only the last one when hidden mode is active
    const displayedComments = useMemo(() => {
        // Filter to only show user comments (is_comment=true)
        const userComments = comments.filter(c => c.is_comment);

        if (focusModeMessagesHidden && userComments.length > 0) {
            return [userComments[userComments.length - 1]];
        }
        return userComments;
    }, [comments, focusModeMessagesHidden]);

    // Fetch item details when itemId changes
    useEffect(() => {
        if (!itemId || !itemType) return;

        const fetchItemDetails = async () => {
            // Only show loading if it's a different item
            if (prevItemIdRef.current !== itemId) {
                setIsLoading(true);
                isInitialLoadRef.current = true;
                previousCommentCountRef.current = 0;
                // Clear reply selection when item changes
                setSelectedReplyComment(null);
            }
            prevItemIdRef.current = itemId;
            // Reset lastCommentIdRef when item changes
            lastCommentIdRef.current = null;

            try {
                const accessToken = Cookies.get('access_token');
                const endpoint = itemType === 'folder'
                    ? `/api/folder/${itemId}/`
                    : `/api/link/${itemId}/`;

                const response = await axios.get(
                    `${BACKEND_API_URL}${endpoint}`,
                    {
                        headers: {
                            Authorization: `Token ${accessToken}`,
                            'Content-Type': 'application/json',
                        },
                    }
                );

                setItemInfo(response.data);

                // Initialize lastCommentIdRef with the max comment ID
                const fetchedComments = itemType === 'folder'
                    ? (response.data.folder_comments || [])
                    : (response.data.link_comments || []);
                if (fetchedComments.length > 0) {
                    lastCommentIdRef.current = Math.max(...fetchedComments.map(c => c.id));
                }
            } catch (error) {
                console.error('Error fetching item details:', error);
                toast.error('Error loading comments');
            } finally {
                setIsLoading(false);
            }
        };

        fetchItemDetails();
    }, [itemId, itemType]);

    // Poll for new comments every 2 seconds
    useEffect(() => {
        if (!itemId || !itemType) return;

        const pollForNewComments = async () => {
            try {
                const accessToken = Cookies.get('access_token');
                const endpoint = itemType === 'folder'
                    ? `/api/folder/${itemId}/`
                    : `/api/link/${itemId}/`;

                const response = await axios.get(
                    `${BACKEND_API_URL}${endpoint}`,
                    {
                        headers: {
                            Authorization: `Token ${accessToken}`,
                            'Content-Type': 'application/json',
                        },
                    }
                );

                if (response.data) {
                    const newComments = itemType === 'folder'
                        ? (response.data.folder_comments || [])
                        : (response.data.link_comments || []);

                    // Check if there are new comments by comparing max comment ID
                    if (newComments.length > 0) {
                        const maxNewId = Math.max(...newComments.map(c => c.id));
                        const hasNewComments = lastCommentIdRef.current === null || maxNewId > lastCommentIdRef.current;

                        if (hasNewComments) {
                            lastCommentIdRef.current = maxNewId;
                            setItemInfo(response.data);
                            // Auto-scroll will be triggered by the comments.length change effect
                        }
                    }
                }
            } catch (error) {
                // Silent fail for polling - don't show error toasts
                console.error('Failed to poll comments:', error);
            }
        };

        const interval = setInterval(pollForNewComments, 2000);
        return () => clearInterval(interval);
    }, [itemId, itemType]);

    // Helper function to scroll to bottom
    const scrollToBottom = useCallback((force = false) => {
        const attemptScroll = () => {
            if (!scrollRef.current) return;

            // Direct scroll to bottom - only manipulate scrollTop, don't use scrollIntoView
            const maxScroll = scrollRef.current.scrollHeight - scrollRef.current.clientHeight;
            scrollRef.current.scrollTop = maxScroll;
        };

        if (force) {
            attemptScroll();
        } else {
            // Wait for next frame to ensure DOM is updated
            requestAnimationFrame(() => {
                attemptScroll();
                // Try again after a short delay to catch any late DOM updates
                setTimeout(attemptScroll, 100);
            });
        }
    }, []);

    // Auto-scroll to bottom when comments change
    useEffect(() => {
        if (scrollRef.current && comments.length > 0) {
            const isNewComment = comments.length > previousCommentCountRef.current;
            const shouldScroll = isInitialLoadRef.current || isNewComment;

            if (shouldScroll) {
                // Use requestAnimationFrame to ensure DOM is updated
                requestAnimationFrame(() => {
                    scrollToBottom(true);
                    // One additional attempt after a short delay
                    const delay = isInitialLoadRef.current ? 200 : 100;
                    setTimeout(() => scrollToBottom(true), delay);
                });
                isInitialLoadRef.current = false;
            }

            previousCommentCountRef.current = comments.length;
        }
    }, [comments.length, itemId, scrollToBottom]);

    // Handle commentId from URL for search result navigation - scroll to and highlight specific comment
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const urlCommentId = params.get('commentId');

        if (urlCommentId && comments.length > 0) {
            const commentId = parseInt(urlCommentId, 10);

            // Set highlighted comment for visual highlighting
            setHighlightedCommentId(commentId);

            // Wait for comments to render, then scroll to the comment
            const scrollToComment = () => {
                const commentElement = commentRefs.current[commentId];
                if (commentElement) {
                    commentElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    });

                    // Remove highlight after 3 seconds
                    setTimeout(() => {
                        setHighlightedCommentId(null);
                    }, 5000);
                }
            };

            // Delay to ensure DOM is fully rendered
            requestAnimationFrame(() => {
                setTimeout(scrollToComment, 300);
            });
        }
    }, [location.search, comments.length]);

    // Focus textarea when reply is selected
    useEffect(() => {
        if (selectedReplyComment && inputRef.current) {
            // Use setTimeout to ensure the DOM is updated
            setTimeout(() => {
                inputRef.current?.focus();
            }, 0);
        }
    }, [selectedReplyComment]);

    // Focus edit textarea when editing starts
    useEffect(() => {
        if (editingCommentId !== null && editInputRef.current) {
            // Use setTimeout to ensure the DOM is updated
            setTimeout(() => {
                editInputRef.current?.focus();
            }, 0);
        }
    }, [editingCommentId]);

    // Fetch users for mention suggestions
    useEffect(() => {
        const fetchUsers = async () => {
            if (mentionQuery.length > 0) {
                try {
                    const accessToken = Cookies.get('access_token');
                    const response = await axios.get(`${BACKEND_API_URL}/api/users_and_tags/`, {
                        headers: {
                            'Authorization': `Token ${accessToken}`,
                            'Content-Type': 'application/json',
                        },
                    });

                    const filtered = filterMentionSuggestions(response.data.users, mentionQuery);

                    setMentionSuggestions(filtered);
                    setShowMentionSuggestions(filtered.length > 0);
                    setSelectedMentionIndex(0);
                } catch (error) {
                    console.error('Error fetching users:', error);
                }
            } else {
                setMentionSuggestions([]);
                setShowMentionSuggestions(false);
            }
        };

        fetchUsers();
    }, [mentionQuery]);

    // Helper function to get full media URL
    const getMediaUrl = (url) => {
        if (!url) return url;
        if (url.startsWith('/')) {
            return `${PRODUCTION_MEDIA_URL}${url}`;
        }
        return url;
    };

    // Get initials for avatar
    const getInitials = (firstName, lastName) => {
        return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
    };

    // Compare calendar days using toDateString() so we match formatDateSeparator (Today/Yesterday) and avoid timezone/parse issues
    const isDifferentDay = (date1, date2) => {
        if (date1 == null || date2 == null) return true;
        const d1 = new Date(date1);
        const d2 = new Date(date2);
        if (Number.isNaN(d1.getTime()) || Number.isNaN(d2.getTime())) return true;
        return d1.toDateString() !== d2.toDateString();
    };

    // Format date separator
    const formatDateSeparator = (date) => {
        const msgDate = new Date(date);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        if (msgDate.toDateString() === today.toDateString()) {
            return "Today";
        } else if (msgDate.toDateString() === yesterday.toDateString()) {
            return "Yesterday";
        } else {
            return msgDate.toLocaleDateString("en-US", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
            });
        }
    };

    // Check if author should be shown (different from previous)
    const shouldShowAuthor = (currentComment, previousComment) => {
        if (!previousComment) return true;
        if (currentComment.user.id !== previousComment.user.id) return true;
        if (isDifferentDay(previousComment.created_at, currentComment.created_at)) return true;
        return false;
    };

    // Check if comment is last in series (for avatar display)
    const isLastInSeries = (currentComment, nextComment) => {
        if (!nextComment) return true;
        if (currentComment.user.id !== nextComment.user.id) return true;
        if (isDifferentDay(currentComment.created_at, nextComment.created_at)) return true;
        return false;
    };


    // Get file extension from URL
    const getFileExtension = (url) => {
        if (!url) return '';
        const match = url.match(/\.([^.]+)$/);
        return match ? match[1].toUpperCase() : '';
    };

    // Get file name from URL
    const getFileName = (url) => {
        if (!url) return 'Attachment';
        const parts = url.split('/');
        const fileName = parts[parts.length - 1];
        // Remove query parameters
        return fileName.split('?')[0] || 'Attachment';
    };

    // File card component - social media style
    const FileCard = ({ mediaUrl, mediaType }) => {
        if (!mediaUrl) return null;

        const url = getMediaUrl(mediaUrl);
        const fileName = getFileName(mediaUrl);
        const fileExt = getFileExtension(mediaUrl);

        // Determine file type icon and color
        const getFileIcon = () => {
            if (mediaType === 'image') {
                return { icon: ImageIcon, color: 'bg-blue-500', text: 'IMG' };
            } else if (mediaType === 'video') {
                return { icon: Video, color: 'bg-purple-500', text: 'VID' };
            } else if (mediaType === 'audio') {
                return { icon: Music, color: 'bg-pink-500', text: 'AUD' };
            } else if (fileExt === 'PDF' || fileExt === 'pdf') {
                return { icon: FileText, color: 'bg-red-500', text: 'PDF' };
            } else if (['DOC', 'DOCX', 'doc', 'docx'].includes(fileExt)) {
                return { icon: FileText, color: 'bg-blue-600', text: 'DOC' };
            } else {
                return { icon: FileText, color: 'bg-gray-500', text: fileExt || 'FILE' };
            }
        };

        const fileInfo = getFileIcon();
        const IconComponent = fileInfo.icon;

        return (
            <div
                className="flex items-center gap-3 p-3 rounded-lg border border-border bg-card hover:bg-muted/50 transition-colors cursor-pointer group"
                onClick={() => {
                    if (mediaType === 'image' || mediaType === 'video') {
                        window.open(url, '_blank');
                    } else {
                        downloadFile(url);
                    }
                }}
            >
                <div className={cn("w-12 h-12 rounded flex items-center justify-center shrink-0", fileInfo.color)}>
                    <IconComponent className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-foreground truncate">
                        {fileName}
                    </div>
                    <div className="text-xs text-muted-foreground">
                        {fileInfo.text} • {mediaType === 'audio' ? 'Audio file' : 'Click to open'}
                    </div>
                </div>
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => {
                        e.stopPropagation();
                        downloadFile(url);
                    }}
                >
                    <MoreVertical className="h-4 w-4" />
                </Button>
            </div>
        );
    };

    // Media preview component - social media style
    const MediaPreview = ({ mediaUrl, mediaType }) => {
        if (!mediaUrl || !mediaType) return null;

        const url = getMediaUrl(mediaUrl);

        if (mediaType === 'image') {
            return (
                <div className="mt-3 max-h-[400px] max-w-full rounded-lg overflow-hidden border border-border bg-muted/20 group cursor-pointer hover:border-primary/40 transition-colors">
                    <img
                        src={url}
                        alt="Attachment"
                        className="h-auto w-auto object-contain max-h-[400px] max-w-full"
                        onClick={() => window.open(url, '_blank')}
                    />
                </div>
            );
        } else if (mediaType === 'video') {
            return (
                <div className="mt-3 max-w-full rounded-lg overflow-hidden border border-border bg-muted/20">
                    <video
                        controls
                        src={url}
                        className="max-h-[400px] w-full rounded-lg"
                    />
                </div>
            );
        } else if (mediaType === 'audio') {
            return (
                <div className="mt-3">
                    <FileCard mediaUrl={mediaUrl} mediaType={mediaType} />
                </div>
            );
        } else {
            return (
                <div className="mt-3">
                    <FileCard mediaUrl={mediaUrl} mediaType={mediaType} />
                </div>
            );
        }
    };

    // Helper function to get relative time
    const getRelativeTime = (timestamp) => {
        const now = new Date();
        const commentDate = new Date(timestamp);
        const diffMs = now - commentDate;
        const diffSecs = Math.floor(diffMs / 1000);
        const diffMins = Math.floor(diffSecs / 60);
        const diffHours = Math.floor(diffMins / 60);
        const diffDays = Math.floor(diffHours / 24);
        const diffMonths = Math.floor(diffDays / 30);
        const diffYears = Math.floor(diffDays / 365);

        if (diffSecs < 60) return `${diffSecs}s ago`;
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 30) return `${diffDays}d ago`;
        if (diffMonths < 12) return `${diffMonths}mo ago`;
        return `${diffYears}y ago`;
    };

    // Helper function to format file size
    const formatFileSize = (bytes) => {
        if (!bytes) return '';
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
        return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
    };

    // Helper function to get human-readable timestamp for filenames
    const getHumanReadableTimestamp = () => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}-${String(now.getSeconds()).padStart(2, '0')}-${String(now.getMilliseconds()).padStart(3, '0')}`;
    };

    // Helper function to add timestamp to filename
    const addTimestampToFilename = (file) => {
        const humanTimestamp = getHumanReadableTimestamp();
        const originalName = file.name;
        const lastDotIndex = originalName.lastIndexOf('.');

        let newFilename;
        if (lastDotIndex === -1) {
            newFilename = `${originalName}_${humanTimestamp}`;
        } else {
            const nameWithoutExt = originalName.substring(0, lastDotIndex);
            const extension = originalName.substring(lastDotIndex);
            newFilename = `${nameWithoutExt}_${humanTimestamp}${extension}`;
        }

        return new File([file], newFilename, {
            type: file.type,
            lastModified: Date.now()
        });
    };

    // Helper function to detect file type by extension as fallback
    const detectFileType = (file) => {
        // First try MIME type
        if (file.type) {
            if (file.type.startsWith('video')) return 'video';
            if (file.type.startsWith('image')) return 'image';
            if (file.type.startsWith('audio')) return 'audio';
        }

        // Fallback to file extension
        const fileName = file.name.toLowerCase();
        const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg', '.ico'];
        const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv', '.flv', '.wmv'];
        const audioExtensions = ['.mp3', '.wav', '.ogg', '.m4a', '.aac', '.flac', '.wma'];

        const extension = fileName.substring(fileName.lastIndexOf('.'));

        if (imageExtensions.includes(extension)) return 'image';
        if (videoExtensions.includes(extension)) return 'video';
        if (audioExtensions.includes(extension)) return 'audio';

        return 'other';
    };

    // Helper function to check if comment was edited
    const isCommentEdited = (commentItem) => {
        if (!commentItem.created_at || !commentItem.updated_at) return false;
        const createdTime = new Date(commentItem.created_at).getTime();
        const updatedTime = new Date(commentItem.updated_at).getTime();
        return (updatedTime - createdTime) > 1000;
    };

    // Helper function to download media
    const handleDownload = async (url, filename) => {
        try {
            const fullUrl = getMediaUrl(url);
            const response = await fetch(fullUrl);
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = filename || 'download';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error('Download failed:', error);
            toast.error('Failed to download file');
        }
    };

    // Handle comment selection (similar to MessageList)
    const handleCommentSelect = useCallback((comment, event) => {
        if (event?.ctrlKey || event?.metaKey) {
            const currentIndex = comments.findIndex((c) => c.id === comment.id);

            if (event?.shiftKey && lastSelectedIndexRef.current !== null) {
                // Bulk select: select all comments between last selected and current
                const start = Math.min(lastSelectedIndexRef.current, currentIndex);
                const end = Math.max(lastSelectedIndexRef.current, currentIndex);
                const rangeComments = comments.slice(start, end + 1);

                setSelectedComments((prev) => {
                    const existingIds = new Set(prev.map((c) => c.id));
                    const newComments = rangeComments.filter((c) => !existingIds.has(c.id));
                    return [...prev, ...newComments];
                });
            } else {
                // Individual toggle
                setSelectedComments((prev) => {
                    const isAlreadySelected = prev.some((c) => c.id === comment.id);
                    if (isAlreadySelected) {
                        return prev.filter((c) => c.id !== comment.id);
                    } else {
                        return [...prev, comment];
                    }
                });
                // Update last selected index for future range selections
                lastSelectedIndexRef.current = currentIndex;
            }
        }
    }, [comments]);

    // Clear selection
    const handleClearSelection = useCallback(() => {
        setSelectedComments([]);
        lastSelectedIndexRef.current = null;
    }, []);

    // Handle comment reply
    const handleCommentReply = useCallback((comment) => {
        setSelectedReplyComment(comment);
        // Focus textarea when reply is selected
        setTimeout(() => {
            inputRef.current?.focus();
        }, 0);
    }, []);

    // Handle cancel reply
    const handleCancelReply = useCallback(() => {
        setSelectedReplyComment(null);
    }, []);

    // Fetch users for mention suggestions in edit mode
    useEffect(() => {
        const fetchUsers = async () => {
            if (editMentionQuery.length > 0 && editingCommentId !== null) {
                try {
                    const accessToken = Cookies.get('access_token');
                    const response = await axios.get(`${BACKEND_API_URL}/api/users_and_tags/`, {
                        headers: {
                            'Authorization': `Token ${accessToken}`,
                            'Content-Type': 'application/json',
                        },
                    });

                    const filtered = filterMentionSuggestions(response.data.users, editMentionQuery);

                    setEditMentionSuggestions(filtered);
                    setShowEditMentionSuggestions(filtered.length > 0);
                    setSelectedEditMentionIndex(0);
                } catch (error) {
                    console.error('Error fetching users:', error);
                }
            } else {
                setEditMentionSuggestions([]);
                setShowEditMentionSuggestions(false);
            }
        };

        fetchUsers();
    }, [editMentionQuery, editingCommentId]);

    // Handle comment edit click
    const handleEditClick = useCallback((comment, e) => {
        e.stopPropagation();
        setEditingCommentId(comment.id);
        setEditedComment(comment.comment || '');
        setEditMentionQuery('');
        setEditMentionSuggestions([]);
        setShowEditMentionSuggestions(false);
        setEditMentionStartIndex(-1);
        setSelectedEditMentionIndex(0);
    }, []);

    // Handle edit mention select
    const handleEditMentionSelect = useCallback((user) => {
        const textBefore = editedComment.substring(0, editMentionStartIndex);
        const textAfter = editedComment.substring(editMentionStartIndex).replace(/@[^\s@]*/, `@${user.username} `);
        setEditedComment(textBefore + textAfter);
        setShowEditMentionSuggestions(false);
        setEditMentionQuery('');
        setTimeout(() => {
            if (editInputRef.current) {
                editInputRef.current.focus();
            }
        }, 0);
    }, [editedComment, editMentionStartIndex]);

    // Handle edit text change
    const handleEditTextChange = useCallback((e) => {
        const newValue = e.target.value;
        setEditedComment(newValue);

        // Check for @ mention
        const cursorPos = e.target.selectionStart || newValue.length;
        const textBeforeCursor = newValue.substring(0, cursorPos);
        const lastAtIndex = textBeforeCursor.lastIndexOf('@');

        if (lastAtIndex !== -1) {
            const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
            if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
                setEditMentionStartIndex(lastAtIndex);
                setEditMentionQuery(textAfterAt);
            } else {
                setShowEditMentionSuggestions(false);
                setEditMentionQuery('');
            }
        } else {
            setShowEditMentionSuggestions(false);
            setEditMentionQuery('');
        }
    }, []);

    // Handle save edit
    const handleSaveEdit = useCallback(async () => {
        if (!editedComment.trim()) {
            toast.error('Comment cannot be empty');
            return;
        }
        if (!editingCommentId) return;

        const commentToEdit = comments.find(c => c.id === editingCommentId);
        if (!commentToEdit) return;

        // Convert both to shortcodes for comparison (backend stores as shortcodes)
        const editedCommentShortcode = convertEmojisToShortcodes(editedComment);

        // Compare shortcode versions to avoid false positives
        if (editedCommentShortcode === commentToEdit.comment) {
            setEditingCommentId(null);
            return;
        }

        setIsUpdating(true);
        try {
            const accessToken = Cookies.get('access_token');
            // Convert emojis to shortcodes for database storage
            const commentToSend = convertEmojisToShortcodes(editedComment);
            await axios.post(
                `${BACKEND_API_URL}/api/item/comment/edit/${editingCommentId}`,
                { comment: commentToSend },
                {
                    headers: {
                        Authorization: `Token ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            // Refresh item info to get updated comment
            const endpoint = itemType === 'folder'
                ? `/api/folder/${itemId}/`
                : `/api/link/${itemId}/`;

            const itemResponse = await axios.get(
                `${BACKEND_API_URL}${endpoint}`,
                {
                    headers: {
                        Authorization: `Token ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            setItemInfo(itemResponse.data);
            setEditingCommentId(null);
            toast.success('Comment updated');
        } catch (error) {
            console.error('Error updating comment:', error);
            toast.error(error.response?.data?.error || 'Failed to update comment');
        } finally {
            setIsUpdating(false);
        }
    }, [editedComment, editingCommentId, comments, itemId, itemType]);

    // Handle edit key down
    const handleEditKeyDown = useCallback((e) => {
        if (showEditMentionSuggestions && editMentionSuggestions.length > 0) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSelectedEditMentionIndex(prev =>
                    prev < editMentionSuggestions.length - 1 ? prev + 1 : prev
                );
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSelectedEditMentionIndex(prev => prev > 0 ? prev - 1 : prev);
            } else if (e.key === 'Enter' || e.key === 'Tab') {
                e.preventDefault();
                const selectedUser = editMentionSuggestions[selectedEditMentionIndex];
                if (selectedUser) handleEditMentionSelect(selectedUser);
            } else if (e.key === 'Escape') {
                e.preventDefault();
                setShowEditMentionSuggestions(false);
                setEditMentionQuery('');
            }
        } else if (e.key === 'Enter' && !e.shiftKey) {
            // Save on Enter (unless mention suggestions are showing or Shift is held)
            e.preventDefault();
            handleSaveEdit();
        }
        // Shift+Enter allows new line (default behavior)
    }, [showEditMentionSuggestions, editMentionSuggestions, selectedEditMentionIndex, handleEditMentionSelect, handleSaveEdit]);

    // Handle cancel edit
    const handleCancelEdit = useCallback(() => {
        setEditingCommentId(null);
        setEditedComment('');
    }, []);

    // Handle delete click
    const handleDeleteClick = useCallback((comment, e) => {
        e.stopPropagation();
        setCommentToDelete(comment);
    }, []);

    // Handle confirm delete
    const handleConfirmDelete = useCallback(async () => {
        if (!commentToDelete) return;

        setIsDeleting(true);
        try {
            const accessToken = Cookies.get('access_token');
            await axios.post(
                `${BACKEND_API_URL}/api/item/comment/delete/${commentToDelete.id}`,
                {},
                {
                    headers: {
                        Authorization: `Token ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            // Refresh item info (deleted comments are filtered out by backend)
            const endpoint = itemType === 'folder'
                ? `/api/folder/${itemId}/`
                : `/api/link/${itemId}/`;

            const response = await axios.get(
                `${BACKEND_API_URL}${endpoint}`,
                {
                    headers: {
                        Authorization: `Token ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            setItemInfo(response.data);
            setCommentToDelete(null);
            toast.success('Comment archived');
        } catch (error) {
            console.error('Error archiving comment:', error);
            toast.error(error.response?.data?.message || 'Failed to archive comment');
        } finally {
            setIsDeleting(false);
        }
    }, [commentToDelete, itemId, itemType]);

    // Handle cancel delete
    const handleCancelDelete = useCallback(() => {
        setCommentToDelete(null);
    }, []);

    // Handle mention select
    const handleMentionSelect = useCallback((user) => {
        const textBefore = comment.substring(0, mentionStartIndex);
        const textAfter = comment.substring(mentionStartIndex).replace(/@[^\s@]*/, `@${user.username} `);
        setComment(textBefore + textAfter);
        setShowMentionSuggestions(false);
        setMentionQuery('');
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }, 0);
    }, [comment, mentionStartIndex]);

    // Convert HTML to markdown-like structured text (same as addMessageToChannel)
    const htmlToStructuredText = useCallback((html) => {
        if (!html) return '';

        // Strip all style and class attributes to prevent theme issues
        const cleanHtml = html
            .replace(/style\s*=\s*["'][^"']*["']/gi, '')
            .replace(/class\s*=\s*["'][^"']*["']/gi, '')
            .replace(/data-[^=]*=["'][^"']*["']/gi, '');

        // Create a temporary div to parse HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = cleanHtml;

        // Helper function to calculate list depth by counting parent lists
        const getListDepth = (node) => {
            let depth = 0;
            let parent = node.parentElement;
            while (parent && parent !== tempDiv) {
                const tagName = parent.tagName?.toLowerCase();
                if (tagName === 'ul' || tagName === 'ol') {
                    depth++;
                }
                parent = parent.parentElement;
            }
            return depth;
        };

        // Helper function to process nodes recursively
        const processNode = (node) => {
            if (node.nodeType === Node.TEXT_NODE) {
                const text = node.textContent || '';
                return text;
            }

            if (node.nodeType !== Node.ELEMENT_NODE) {
                return '';
            }

            const tagName = node.tagName?.toLowerCase();
            const children = Array.from(node.childNodes);

            // Process children
            let content = '';

            for (const child of children) {
                if (child.nodeType === Node.ELEMENT_NODE) {
                    // Process child node (depth is calculated via getListDepth when needed)
                    content += processNode(child);
                } else if (child.nodeType === Node.TEXT_NODE) {
                    const text = child.textContent || '';
                    // Preserve text in list items, trim elsewhere
                    if (tagName === 'li') {
                        content += text;
                    } else {
                        content += text.trim();
                    }
                }
            }

            switch (tagName) {
                case 'p':
                    return content.trim() ? `${content.trim()}\n\n` : '';

                case 'div':
                    return content.trim() ? `${content.trim()}\n\n` : '';

                case 'br':
                    return '\n';

                case 'ul':
                case 'ol':
                    // Lists return their content (already processed)
                    return content;

                case 'li':
                    const depth = getListDepth(node);
                    // Markdown requires 2 spaces per nesting level
                    // Level 1 (depth=1): 0 spaces, Level 2 (depth=2): 2 spaces, Level 3 (depth=3): 4 spaces, etc.
                    const indent = '  '.repeat(Math.max(0, depth - 1));
                    const parentTag = node.parentElement?.tagName?.toLowerCase();
                    const marker = parentTag === 'ol' ? '1.' : '-';

                    // Split content into text and nested lists
                    let textContent = '';
                    let nestedListContent = '';

                    // Check if this li contains nested lists
                    const hasNestedList = children.some(child => {
                        const childTag = child.tagName?.toLowerCase();
                        return childTag === 'ul' || childTag === 'ol';
                    });

                    if (hasNestedList) {
                        // Separate text from nested lists
                        for (const child of children) {
                            if (child.nodeType === Node.TEXT_NODE) {
                                const text = child.textContent || '';
                                textContent += text.trim() ? text.trim() + ' ' : '';
                            } else if (child.nodeType === Node.ELEMENT_NODE) {
                                const childTag = child.tagName?.toLowerCase();
                                if (childTag === 'ul' || childTag === 'ol') {
                                    // Nested list - process it (it will calculate its own depth)
                                    // The nested list items will have depth+1, so they'll be indented by 2 more spaces
                                    nestedListContent += processNode(child);
                                } else if (childTag === 'p' || childTag === 'div') {
                                    const processed = processNode(child).trim();
                                    textContent += processed ? processed + ' ' : '';
                                } else if (childTag === 'strong' || childTag === 'b' || childTag === 'em' || childTag === 'i') {
                                    textContent += processNode(child);
                                } else {
                                    textContent += processNode(child);
                                }
                            }
                        }

                        textContent = textContent.trim();
                        // Format: marker text, then nested list on next line
                        // Nested list items will already have proper 2-space indentation from their depth calculation
                        if (textContent) {
                            return `${indent}${marker} ${textContent}\n${nestedListContent}`;
                        } else {
                            return `${indent}${marker}\n${nestedListContent}`;
                        }
                    } else {
                        // Simple list item with no nested lists
                        const cleanContent = content.trim();
                        return `${indent}${marker} ${cleanContent}\n`;
                    }

                case 'strong':
                case 'b':
                    return `**${content}**`;

                case 'em':
                case 'i':
                    return `*${content}*`;

                case 'code':
                    // Don't add backticks if inside pre
                    if (node.parentElement?.tagName?.toLowerCase() === 'pre') {
                        return content;
                    }
                    return `\`${content}\``;

                case 'pre':
                    return `\n\`\`\`\n${content}\n\`\`\`\n`;

                case 'h1':
                    return `# ${content.trim()}\n\n`;
                case 'h2':
                    return `## ${content.trim()}\n\n`;
                case 'h3':
                    return `### ${content.trim()}\n\n`;
                case 'h4':
                    return `#### ${content.trim()}\n\n`;
                case 'h5':
                    return `##### ${content.trim()}\n\n`;
                case 'h6':
                    return `###### ${content.trim()}\n\n`;

                case 'blockquote':
                    return content.split('\n').map(line => line.trim() ? `> ${line.trim()}` : '').join('\n') + '\n\n';

                case 'a':
                    const href = node.getAttribute('href') || '';
                    const linkText = content || href;
                    return href && href !== linkText ? `[${linkText}](${href})` : linkText;

                case 'table':
                    return `\n${content}\n`;

                case 'thead':
                case 'tbody':
                    return content;

                case 'tr':
                    return `${content}|\n`;

                case 'th':
                case 'td':
                    return `${content.trim()} | `;

                case 'hr':
                    return '\n---\n\n';

                default:
                    return content;
            }
        };

        let result = processNode(tempDiv);

        // Clean up excessive newlines (max 2 consecutive, but preserve list structure)
        // Don't collapse newlines that are part of list structure
        result = result.replace(/\n{4,}/g, '\n\n\n');

        // Clean up trailing whitespace but preserve structure
        result = result.trim();

        return result;
    }, []);

    // Handle paste events to capture rich content (same as addMessageToChannel)
    const handlePaste = useCallback((e) => {
        e.preventDefault();

        const clipboardData = e.clipboardData || window.clipboardData;

        // Check for image data in clipboard first (for screenshots/copied images)
        if (clipboardData.items && clipboardData.items.length > 0) {
            const items = Array.from(clipboardData.items);
            const imageItem = items.find(item => item.type.startsWith('image/'));

            if (imageItem) {
                let file = imageItem.getAsFile();
                if (file) {
                    // Ensure file has a name (clipboard images might not have one)
                    if (!file.name) {
                        const extension = imageItem.type.split('/')[1] || 'png';
                        file = new File([file], `screenshot.${extension}`, { type: imageItem.type });
                    }
                    // Process image like file upload - show annotation dialog
                    const timestampedFile = addTimestampToFilename(file);
                    const detectedType = detectFileType(timestampedFile);

                    if (detectedType === 'image') {
                        // Show annotation dialog for images
                        const reader = new FileReader();
                        reader.onload = (event) => {
                            setAnnotationImage(event.target.result);
                            setShowAnnotationDialog(true);
                        };
                        reader.readAsDataURL(timestampedFile);
                        setMediaFile(timestampedFile);
                    }
                    return; // Don't process text if image was found
                }
            }
        }

        // Fallback: Check clipboardData.files as alternative (for older browsers)
        if (clipboardData.files && clipboardData.files.length > 0) {
            const files = Array.from(clipboardData.files);
            const imageFile = files.find(file => file.type.startsWith('image/'));

            if (imageFile) {
                // Ensure file has a name (clipboard images might not have one)
                let fileToProcess = imageFile;
                if (!imageFile.name) {
                    const extension = imageFile.type.split('/')[1] || 'png';
                    fileToProcess = new File([imageFile], `screenshot.${extension}`, { type: imageFile.type });
                }
                // Process image like file upload
                const timestampedFile = addTimestampToFilename(fileToProcess);
                const detectedType = detectFileType(timestampedFile);

                if (detectedType === 'image') {
                    // Show annotation dialog for images
                    const reader = new FileReader();
                    reader.onload = (event) => {
                        setAnnotationImage(event.target.result);
                        setShowAnnotationDialog(true);
                    };
                    reader.readAsDataURL(timestampedFile);
                    setMediaFile(timestampedFile);
                }
                return; // Don't process text if image was found
            }
        }

        // No image found - process text/HTML as before
        const html = clipboardData.getData('text/html');
        const plainText = clipboardData.getData('text/plain');

        // Get current cursor position
        const textarea = e.target;
        const start = textarea.selectionStart || 0;
        const end = textarea.selectionEnd || 0;
        const currentValue = comment;

        let textToInsert = '';

        // If HTML is available, convert it to structured markdown
        if (html && html.trim()) {
            textToInsert = htmlToStructuredText(html);
        } else if (plainText) {
            // Fallback to plain text if no HTML
            textToInsert = plainText;
        }

        // Insert the text at cursor position
        const newValue =
            currentValue.substring(0, start) +
            textToInsert +
            currentValue.substring(end);

        setComment(newValue);

        // Restore cursor position after the inserted text
        setTimeout(() => {
            if (inputRef.current) {
                const newCursorPos = start + textToInsert.length;
                inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
                inputRef.current.focus();
            }
        }, 0);
    }, [comment, htmlToStructuredText, addTimestampToFilename, detectFileType]);

    // Handle Escape key to clear selection
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && selectedComments.length > 0) {
                const tag = e.target.tagName.toLowerCase();
                if (tag !== 'input' && tag !== 'textarea' && !e.target.isContentEditable) {
                    handleClearSelection();
                }
            }
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [selectedComments.length, handleClearSelection]);

    // Generate folder title from comment
    const generateFolderTitleFromComment = useCallback((comment) => {
        if (comment.comment && comment.comment.trim()) {
            // Use comment text as title (limit to 100 chars)
            const title = comment.comment.trim();
            return title.length > 100 ? title.substring(0, 100) + '...' : title;
        } else if (comment.media_type === 'audio' && comment.media_url) {
            // Extract filename from URL
            const fileName = getFileName(comment.media_url);
            return `Audio Recording - ${fileName}`;
        } else if (comment.media_type) {
            // Use media type and timestamp
            const date = new Date(comment.created_at);
            return `${comment.media_type} from comment - ${date.toLocaleString()}`;
        } else {
            // Fallback
            const date = new Date(comment.created_at);
            return `Comment from ${date.toLocaleString()}`;
        }
    }, []);

    // Promote selected comments to folders
    const handlePromoteComments = useCallback(async () => {
        if (selectedComments.length === 0 || !itemId || itemType !== 'folder') {
            return;
        }

        setIsPromoting(true);
        toast.loading(`Promoting ${selectedComments.length} comment(s) to folders...`);

        try {
            const accessToken = Cookies.get('access_token');
            const PRODUCTION_MEDIA_URL = 'https://onemap.morphlelabs.com';

            // Prepare folders to create
            const foldersToCreate = selectedComments.map(comment => ({
                name: generateFolderTitleFromComment(comment)
            }));

            // Bulk create folders
            const bulkResponse = await axios.post(
                `${BACKEND_API_URL}/api/folders/bulk_create/`,
                { folders: foldersToCreate, parent_id: itemId },
                {
                    headers: {
                        Authorization: `Token ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            // Handle response - check both response.data and direct response
            const responseData = bulkResponse.data || bulkResponse;
            const createdFolders = responseData.folders || [];

            // Debug logging
            console.log('Bulk create response:', responseData);
            console.log('Created folders:', createdFolders);
            console.log('Folders count:', createdFolders.length);

            // Add comments to each created folder in parallel
            const commentPromises = createdFolders.map(async (folder, i) => {
                const comment = selectedComments[i];
                try {
                    const commentFormData = new FormData();
                    const commentBody = comment.comment || `Promoted from comment by ${comment.user.first_name} ${comment.user.last_name}`;
                    // Convert emojis to shortcodes for storage (comment.comment may already be in shortcode format from DB, but convert to be safe)
                    const commentToSend = convertEmojisToShortcodes(commentBody);
                    commentFormData.append('comment', commentToSend);

                    if (comment.media_url) {
                        // Use full URL for media
                        const mediaUrl = comment.media_url.startsWith('/')
                            ? `${PRODUCTION_MEDIA_URL}${comment.media_url}`
                            : comment.media_url;
                        commentFormData.append('media_type', comment.media_type || 'other');
                        commentFormData.append('media_url', mediaUrl);
                    } else if (comment.comment) {
                        // Create text file from comment
                        const blob = new Blob([comment.comment], { type: 'text/plain' });
                        const safeTitle = (folder.name || 'comment').replace(/[^a-z0-9._-]/gi, '_').slice(0, 60);
                        commentFormData.append('media_file', blob, `${safeTitle}_comment.txt`);
                        commentFormData.append('media_type', 'other');
                    }

                    await axios.post(
                        `${BACKEND_API_URL}/api/item/comment/add/${folder.id}`,
                        commentFormData,
                        {
                            headers: {
                                Authorization: `Token ${accessToken}`,
                                'Content-Type': 'multipart/form-data',
                            },
                        }
                    );
                    return { success: true, folderId: folder.id };
                } catch (error) {
                    console.error('Failed to add comment to folder:', error);
                    return { success: false, folderId: folder.id };
                }
            });

            const commentResults = await Promise.all(commentPromises);
            const successCount = commentResults.filter(r => r.success).length;
            const folderCount = createdFolders.length;

            toast.dismiss();
            if (successCount === folderCount && folderCount > 0) {
                toast.success(`Promoted ${folderCount} comment(s) to folders`);
            } else if (successCount > 0) {
                toast.success(`Promoted ${successCount} of ${folderCount} comment(s) to folders`);
            } else {
                toast.error('Failed to promote comments to folders');
            }

            // Clear selection
            setSelectedComments([]);
            lastSelectedIndexRef.current = null;

            // Refresh children in parent component (await to ensure it completes)
            if (onPromoteComplete) {
                await onPromoteComplete();
            }

            // Refresh item info to update comment count
            try {
                const endpoint = itemType === 'folder'
                    ? `/api/folder/${itemId}/`
                    : `/api/link/${itemId}/`;

                const response = await axios.get(
                    `${BACKEND_API_URL}${endpoint}`,
                    {
                        headers: {
                            Authorization: `Token ${accessToken}`,
                            'Content-Type': 'application/json',
                        },
                    }
                );
                setItemInfo(response.data);
            } catch (error) {
                console.error('Error refreshing item info:', error);
            }
        } catch (error) {
            console.error('Failed to promote comments:', error);
            toast.dismiss();
            toast.error('Failed to promote comments to folders');
        } finally {
            setIsPromoting(false);
        }
    }, [selectedComments, itemId, itemType, generateFolderTitleFromComment, onPromoteComplete]);

    // Date separator component - matching MessageList style
    const DateSeparator = ({ date }) => (
        <div className="flex items-center gap-3 my-5">
            <Separator className="flex-1" />
            <span className="text-xs font-semibold text-primary bg-background px-3 py-1 rounded-full border border-primary/30">
                {formatDateSeparator(date)}
            </span>
            <Separator className="flex-1" />
        </div>
    );

    // Handle comment submission
    const handleCommentSubmit = useCallback(async (e) => {
        // Allow Shift+Enter for new lines, Enter alone to submit
        // Handle both keyboard events and button clicks
        if (e && e.key !== undefined) {
            if (e.key !== 'Enter' || !comment.trim()) return;
            if (e.shiftKey) return; // Allow Shift+Enter for new lines
        } else {
            // Button click - just check if comment exists
            if (!comment.trim()) return;
        }

        setSavingComment(true);
        try {
            const accessToken = Cookies.get('access_token');
            const formData = new FormData();
            // Convert emojis to shortcodes (e.g., 🔥 → :fire:) for database storage
            const commentToSend = convertEmojisToShortcodes(comment || '');
            formData.append('comment', commentToSend);
            formData.append('media_type', '');

            // Include replied_to_comment_id if replying to a comment
            if (selectedReplyComment && selectedReplyComment.id) {
                formData.append('replied_to_comment_id', selectedReplyComment.id);
            }

            await axios.post(
                `${BACKEND_API_URL}/api/item/comment/add/${itemId}`,
                formData,
                {
                    headers: {
                        Authorization: `Token ${accessToken}`,
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            // Refresh comments
            const endpoint = itemType === 'folder'
                ? `/api/folder/${itemId}/`
                : `/api/link/${itemId}/`;

            const response = await axios.get(
                `${BACKEND_API_URL}${endpoint}`,
                {
                    headers: {
                        Authorization: `Token ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            setItemInfo(response.data);
            setComment("");
            // Clear reply selection after sending
            setSelectedReplyComment(null);
            toast.success("Comment added");

            // Refocus input after comment is sent
            setTimeout(() => {
                inputRef.current?.focus();
            }, 0);

            // Force scroll to bottom after comment is added
            // Use requestAnimationFrame to ensure DOM is updated
            requestAnimationFrame(() => {
                scrollToBottom(true);
                // One additional attempt after a short delay
                setTimeout(() => scrollToBottom(true), 100);
            });
        } catch (error) {
            console.error('Error adding comment:', error);
            toast.error("Error adding comment");
        } finally {
            setSavingComment(false);
        }
    }, [comment, itemId, itemType, scrollToBottom, selectedReplyComment]);

    // Handle file upload (called from preview dialog)
    const handleFileUpload = useCallback(async (file) => {
        if (!file) return;

        setSavingComment(true);
        try {
            const accessToken = Cookies.get('access_token');
            const formData = new FormData();
            // Convert emojis to shortcodes for database storage
            const commentToSend = convertEmojisToShortcodes(comment || `Attachment: ${file.name}`);
            formData.append('comment', commentToSend);
            formData.append('media_file', file);

            // Determine media type
            const detectedType = detectFileType(file);
            formData.append('media_type', detectedType);

            // Add media_size for file size tracking
            if (file.size) {
                formData.append('media_size', file.size.toString());
            }

            // Include replied_to_comment_id if replying to a comment
            if (selectedReplyComment && selectedReplyComment.id) {
                formData.append('replied_to_comment_id', selectedReplyComment.id);
            }

            await axios.post(
                `${BACKEND_API_URL}/api/item/comment/add/${itemId}`,
                formData,
                {
                    headers: {
                        Authorization: `Token ${accessToken}`,
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            // Refresh comments
            const endpoint = itemType === 'folder'
                ? `/api/folder/${itemId}/`
                : `/api/link/${itemId}/`;

            const response = await axios.get(
                `${BACKEND_API_URL}${endpoint}`,
                {
                    headers: {
                        Authorization: `Token ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            setItemInfo(response.data);
            setComment("");
            setMediaFile(null);
            setMediaType('');
            setIsMediaSelected(false);
            setShowRecordingDialog(false);
            setScreenshotCanvas(null);
            setScreenshotQuality('0.75');
            // Clear reply selection after sending
            setSelectedReplyComment(null);
            toast.success("Attachment added");

            // Refocus input after attachment is sent
            setTimeout(() => {
                inputRef.current?.focus();
            }, 0);

            // Force scroll to bottom after attachment is added
            // Use requestAnimationFrame to ensure DOM is updated
            requestAnimationFrame(() => {
                scrollToBottom(true);
                // One additional attempt after a short delay
                setTimeout(() => scrollToBottom(true), 100);
            });
        } catch (error) {
            console.error('Error uploading file:', error);
            toast.error("Error uploading attachment");
        } finally {
            setSavingComment(false);
        }
    }, [comment, itemId, itemType, scrollToBottom, selectedReplyComment]);

    // 500MB file size limit
    const MAX_FILE_SIZE = 500 * 1024 * 1024; // 500MB in bytes

    // Handle file input change - show annotation dialog for images, preview for others
    const handleFileChange = useCallback((e) => {
        const file = e.target.files?.[0];
        if (file) {
            // Check file size limit (500MB)
            if (file.size > MAX_FILE_SIZE) {
                toast.error(`File size exceeds 500MB limit. Your file is ${formatFileSize(file.size)}.`);
                // Reset input
                if (e.target) {
                    e.target.value = '';
                }
                return;
            }

            const timestampedFile = addTimestampToFilename(file);
            const detectedType = detectFileType(file);

            if (detectedType === 'video') {
                setMediaFile(timestampedFile);
                setIsMediaSelected(true);
                setMediaType('video');
                setShowRecordingDialog(true);
            } else if (detectedType === 'image') {
                // Show annotation dialog for all image uploads (mobile and desktop)
                const reader = new FileReader();
                reader.onload = (event) => {
                    setAnnotationImage(event.target.result);
                    setShowAnnotationDialog(true);
                };
                reader.readAsDataURL(file);
                setMediaFile(timestampedFile);
            } else if (detectedType === 'audio') {
                setMediaFile(timestampedFile);
                setIsMediaSelected(true);
                setMediaType('audio');
                setShowRecordingDialog(true);
            } else {
                // Support all other file types (PDFs, docs, archives, etc.)
                setMediaFile(timestampedFile);
                setIsMediaSelected(true);
                setMediaType('other');
                setShowRecordingDialog(true);
            }

            if (videoRef.current) {
                videoRef.current.load();
            }
        }
        // Reset input
        if (e.target) {
            e.target.value = '';
        }
    }, []);

    // Handle recording complete callbacks
    const handleRecordingComplete = useCallback((blob) => {
        const humanTimestamp = getHumanReadableTimestamp();
        const audioFile = new File([blob], `audio_recording_${humanTimestamp}.wav`, {
            type: 'audio/wav',
            lastModified: Date.now()
        });
        setMediaFile(audioFile);
        setIsMediaSelected(true);
        setMediaType('audio');
        setShowRecordingDialog(true);
    }, []);

    const handleVideoRecordingComplete = useCallback((blob) => {
        const humanTimestamp = getHumanReadableTimestamp();
        const videoFile = new File([blob], `video_recording_${humanTimestamp}.webm`, {
            type: 'video/webm',
            lastModified: Date.now()
        });
        setMediaFile(videoFile);
        setIsMediaSelected(true);
        setMediaType('video');
        setShowRecordingDialog(true);
    }, []);

    const handleScreenRecordingComplete = useCallback((blob) => {
        const humanTimestamp = getHumanReadableTimestamp();
        const videoFile = new File([blob], `screen_recording_${humanTimestamp}.webm`, {
            type: 'video/webm',
            lastModified: Date.now()
        });
        setMediaFile(videoFile);
        setIsMediaSelected(true);
        setMediaType('video');
        setShowRecordingDialog(true);
    }, []);

    const handleScreenshotComplete = useCallback((canvas) => {
        // Convert canvas to data URL and show annotation dialog
        const dataUrl = canvas.toDataURL('image/png');
        setAnnotationImage(dataUrl);
        setScreenshotCanvas(canvas);
        setShowAnnotationDialog(true);
    }, []);

    // Generate screenshot blob with quality
    const generateScreenshotBlob = useCallback((canvas, quality) => {
        canvas.toBlob((blob) => {
            const humanTimestamp = getHumanReadableTimestamp();
            const screenshotFile = new File([blob], `screenshot_${humanTimestamp}.jpg`, {
                type: 'image/jpeg',
                lastModified: Date.now()
            });
            setMediaFile(screenshotFile);
            setIsMediaSelected(true);
            setMediaType('image');
            setShowRecordingDialog(true);
        }, 'image/jpeg', quality);
    }, []);

    // Handle screenshot quality change
    const handleScreenshotQualityChange = useCallback((value) => {
        if (!value) return;
        const quality = parseFloat(value);
        setScreenshotQuality(value);
        if (screenshotCanvas) {
            generateScreenshotBlob(screenshotCanvas, quality);
        }
    }, [screenshotCanvas, generateScreenshotBlob]);

    // Handle send from preview dialog
    const handleSendRecording = useCallback(() => {
        setShowRecordingDialog(false);
        setScreenshotCanvas(null);
        setScreenshotQuality('0.75');
        if (mediaFile) {
            handleFileUpload(mediaFile);
        }
    }, [mediaFile, handleFileUpload]);

    // Handle cancel from preview dialog
    const handleCancelRecording = useCallback(() => {
        setShowRecordingDialog(false);
        setScreenshotCanvas(null);
        setScreenshotQuality('0.75');
        clearMedia();
    }, []);

    // Clear media state
    const clearMedia = useCallback(() => {
        setMediaFile(null);
        setMediaType('');
        setIsMediaSelected(false);
        setAnnotationImage(null);
        imageLoadedRef.current = false;
        setBrushSize('10');
        if (clearRecordingRef.current) clearRecordingRef.current();
        if (clearVideoRecordingRef.current) clearVideoRecordingRef.current();
        if (clearScreenRecordingRef.current) clearScreenRecordingRef.current();
    }, []);

    // Handle download from preview dialog
    const handleDownloadRecording = useCallback(() => {
        if (mediaFile) {
            const url = URL.createObjectURL(mediaFile);
            const link = document.createElement('a');
            link.href = url;
            link.download = mediaFile.name;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            toast.success('Recording downloaded!');
        }
    }, [mediaFile]);

    // Handle upload button click
    const handleUploadButtonClick = useCallback(() => {
        generalFileInputRef.current?.click();
    }, []);

    // Loading state
    if (isLoading) {
        return (
            <div className="h-full flex items-center justify-center">
                <div className="flex flex-col items-center gap-2">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    <span className="text-sm text-muted-foreground">Loading comments...</span>
                </div>
            </div>
        );
    }

    return (
        <TooltipProvider>
            <div className="h-full flex flex-col bg-muted/50 dark:bg-muted/30">
                {/* Comments scroll area */}
                <div
                    ref={scrollRef}
                    className={cn(
                        "w-full overflow-auto relative",
                        !mobileScreen && "px-2",
                        focusModeMessagesHidden ? "shrink-0" : "flex-1 min-h-0"
                    )}
                >
                    <div className={cn(
                        "space-y-0",
                        focusModeMessagesHidden ? "py-2" : "py-4"
                    )}>
                        {/* Empty state - only show when not in hidden mode */}
                        {comments.length === 0 && !isLoading && !focusModeMessagesHidden && (
                            <div className="h-full min-h-[400px] flex items-center justify-center p-8">
                                <div className="text-center max-w-md space-y-4">
                                    <div className="flex justify-center">
                                        <div className="rounded-full bg-primary/10 p-6">
                                            <MessageSquare className="h-12 w-12 text-primary" />
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <h3 className="text-xl font-semibold">No comments yet</h3>
                                        <p className="text-sm text-muted-foreground">
                                            Be the first to start the conversation. Share your thoughts, ideas, or just say hello!
                                        </p>
                                    </div>
                                    <div className="pt-2">
                                        <Badge variant="outline" className="gap-2">
                                            <Sparkles className="h-3 w-3" />
                                            Start typing below to send a comment
                                        </Badge>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Comments list - MessageCard style */}
                        {displayedComments.map((commentItem, index) => {
                            // Use original index for proper context when in hidden mode
                            const originalIndex = focusModeMessagesHidden
                                ? comments.length - 1
                                : index;

                            // In hidden mode, always show author for the single displayed message
                            const showAuthor = focusModeMessagesHidden
                                ? true
                                : shouldShowAuthor(
                                    commentItem,
                                    originalIndex > 0 ? comments[originalIndex - 1] : null
                                );

                            // Show date separator only when the *previous displayed* comment is on a different day (so same-day comments don't get repeated "Today" dividers)
                            const showDateSeparator = focusModeMessagesHidden
                                ? true
                                : (index === 0 || isDifferentDay(
                                    displayedComments[index - 1]?.created_at,
                                    commentItem.created_at
                                ));

                            // In hidden mode, always show avatar for the single message
                            const showAvatar = focusModeMessagesHidden
                                ? true
                                : isLastInSeries(
                                    commentItem,
                                    originalIndex < comments.length - 1 ? comments[originalIndex + 1] : null
                                );

                            const isOwnComment = user && commentItem.user.username === user.username;
                            const commentAlignment = isOwnComment ? 'justify-end' : 'justify-start';
                            const flexDirection = isOwnComment ? 'flex-row-reverse' : 'flex-row';

                            return (
                                <React.Fragment key={commentItem.id}>
                                    {/* Date separator */}
                                    {showDateSeparator && (
                                        <DateSeparator date={commentItem.created_at} />
                                    )}

                                    {/* Comment - WhatsApp-style bubble */}
                                    <div className={cn("flex w-full px-2", commentAlignment)}>
                                        <div className={cn("flex gap-1.5 max-w-[85%] items-start", flexDirection)}>
                                            {/* Avatar - only show for others' comments */}
                                            {showAvatar && !isOwnComment && (
                                                <Avatar className="h-6 w-6 mt-0.5 shrink-0">
                                                    <AvatarImage src={commentItem.user.picture} />
                                                    <AvatarFallback className="bg-primary text-primary-foreground text-[9px] font-medium">
                                                        {commentItem.user.first_name?.[0] || ''}{commentItem.user.last_name?.[0] || ''}
                                                    </AvatarFallback>
                                                </Avatar>
                                            )}

                                            {/* Comment Bubble */}
                                            <div
                                                ref={(el) => {
                                                    commentRefs.current[commentItem.id] = el;
                                                }}
                                                data-comment-id={commentItem.id}
                                                onClick={(e) => handleCommentSelect(commentItem, e)}
                                                className={cn(
                                                    "group relative cursor-pointer transition-all duration-150 min-w-[20vw] max-w-full",
                                                    showAuthor ? "mb-3" : "mb-1.5",
                                                    !showAvatar && !isOwnComment && "ml-8",
                                                    isOwnComment
                                                        ? "bg-primary/15 dark:bg-primary/25 rounded-lg rounded-br-sm"
                                                        : "bg-card rounded-lg rounded-bl-sm",
                                                    "shadow-sm overflow-hidden",
                                                    selectedComments.some(c => c.id === commentItem.id) && "ring-2 ring-primary ring-offset-2",
                                                    selectedReplyComment?.id === commentItem.id && "ring-2 ring-primary ring-offset-2",
                                                    highlightedCommentId === commentItem.id && "ring-2 ring-blue-500 ring-offset-2"
                                                )}
                                            >
                                                {/* Author Header */}
                                                {showAuthor && (
                                                    <div className="px-2 pt-0.5 pb-0">
                                                        <span className="text-[10px] font-semibold text-foreground/70">
                                                            {commentItem.user.first_name} {commentItem.user.last_name}
                                                        </span>
                                                    </div>
                                                )}

                                                {/* Image Media */}
                                                {commentItem.media_type === 'image' && (
                                                    <div className="relative overflow-hidden rounded-t-lg">
                                                        <img
                                                            src={getMediaUrl(commentItem.media_url)}
                                                            alt="Attachment"
                                                            className="w-full max-h-[180px] object-cover cursor-pointer"
                                                            onClick={() => window.open(getMediaUrl(commentItem.media_url), '_blank')}
                                                        />
                                                        <div className="absolute top-1 right-1">
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        className="h-6 w-6 bg-black/60 hover:bg-black/80 text-white"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleDownload(commentItem.media_url, `image_${commentItem.id}.jpg`);
                                                                        }}
                                                                    >
                                                                        <Download className="h-3 w-3" />
                                                                    </Button>
                                                                </TooltipTrigger>
                                                                <TooltipContent>Download Image</TooltipContent>
                                                            </Tooltip>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Video Media */}
                                                {commentItem.media_type === 'video' && (
                                                    <div className="relative overflow-hidden rounded-t-lg">
                                                        <video
                                                            controls
                                                            src={getMediaUrl(commentItem.media_url)}
                                                            className="w-full max-h-[180px] object-cover"
                                                        />
                                                        <div className="absolute top-1 right-1">
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        className="h-6 w-6 bg-black/60 hover:bg-black/80 text-white"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleDownload(commentItem.media_url, `video_${commentItem.id}.mp4`);
                                                                        }}
                                                                    >
                                                                        <Download className="h-3 w-3" />
                                                                    </Button>
                                                                </TooltipTrigger>
                                                                <TooltipContent>Download Video</TooltipContent>
                                                            </Tooltip>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Audio Media */}
                                                {commentItem.media_type === 'audio' && (
                                                    <div className="pt-1 px-2 relative">
                                                        <ReactAudioPlayer
                                                            src={getMediaUrl(commentItem.media_url)}
                                                            controls
                                                            style={{ width: '100%', height: '32px' }}
                                                        />
                                                        <div className="absolute top-1 right-2">
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        className="h-6 w-6 bg-black/60 hover:bg-black/80 text-white"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleDownload(commentItem.media_url, `audio_${commentItem.id}.wav`);
                                                                        }}
                                                                    >
                                                                        <Download className="h-3 w-3" />
                                                                    </Button>
                                                                </TooltipTrigger>
                                                                <TooltipContent>Download Audio</TooltipContent>
                                                            </Tooltip>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Other file types */}
                                                {commentItem.media_type === 'other' && commentItem.media_url && (
                                                    <div className="pt-1 px-2">
                                                        <FileCard mediaUrl={commentItem.media_url} mediaType={commentItem.media_type} />
                                                    </div>
                                                )}

                                                {/* Content */}
                                                <div className={cn(
                                                    "relative px-2 flex flex-col",
                                                    showAuthor ? "pt-0" : "py-1",
                                                    !showAuthor && "min-h-[24px]"
                                                )}>
                                                    {/* Quoted Reply Preview - shows if this comment is a reply */}
                                                    {(() => {
                                                        // Check if backend returns replied_to_comment object directly
                                                        let repliedToComment = commentItem.replied_to_comment;

                                                        // If not, try to find it by replied_to_comment_id
                                                        if (!repliedToComment && commentItem.replied_to_comment_id) {
                                                            repliedToComment = comments.find(c => c.id === commentItem.replied_to_comment_id);
                                                        }

                                                        if (repliedToComment) {
                                                            const repliedToUser = repliedToComment.user || repliedToComment.author;
                                                            const repliedToText = repliedToComment.comment || repliedToComment.message || '';
                                                            const repliedToMediaType = repliedToComment.media_type;

                                                            return (
                                                                <div
                                                                    className={cn(
                                                                        "mb-2.5 px-3 py-2 rounded-md border-l-4 cursor-pointer transition-all",
                                                                        "shadow-sm hover:shadow-md hover:scale-[1.01]",
                                                                        isOwnComment
                                                                            ? "bg-primary/30 border-primary dark:bg-primary/20 hover:bg-primary/40"
                                                                            : "bg-muted border-muted-foreground/50 hover:bg-muted/80"
                                                                    )}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        // Scroll to the replied comment if we have its ID
                                                                        const repliedId = repliedToComment.id;
                                                                        if (repliedId) {
                                                                            const repliedElement = commentRefs.current[repliedId];
                                                                            if (repliedElement) {
                                                                                repliedElement.scrollIntoView({
                                                                                    behavior: 'smooth',
                                                                                    block: 'center'
                                                                                });
                                                                                // Highlight briefly
                                                                                setTimeout(() => {
                                                                                    repliedElement.classList.add('ring-2', 'ring-primary', 'ring-offset-2');
                                                                                    setTimeout(() => {
                                                                                        repliedElement.classList.remove('ring-2', 'ring-primary', 'ring-offset-2');
                                                                                    }, 2000);
                                                                                }, 300);
                                                                            }
                                                                        }
                                                                    }}
                                                                >
                                                                    <div className="flex items-center gap-1.5 mb-1">
                                                                        <span className="text-[11px] font-bold text-foreground">
                                                                            ↩ {repliedToUser?.first_name || ''} {repliedToUser?.last_name || ''}
                                                                        </span>
                                                                    </div>
                                                                    <div className="text-[12px] text-foreground/80 line-clamp-2 font-medium">
                                                                        {repliedToMediaType && (
                                                                            <span className="italic">
                                                                                {repliedToMediaType === 'image' && '📷 Image'}
                                                                                {repliedToMediaType === 'video' && '🎥 Video'}
                                                                                {repliedToMediaType === 'audio' && '🎤 Audio'}
                                                                                {repliedToMediaType === 'other' && '📎 File'}
                                                                                {repliedToText ? ': ' : ''}
                                                                            </span>
                                                                        )}
                                                                        {convertToEmojis(repliedToText) || ''}
                                                                    </div>
                                                                </div>
                                                            );
                                                        }
                                                        return null;
                                                    })()}

                                                    {/* Comment text */}
                                                    {commentItem.comment && (
                                                        <div className="break-words max-w-full" style={{ overflowWrap: 'anywhere', wordBreak: 'break-word', display: 'block' }}>
                                                            <MarkdownText className="text-[13px] leading-snug text-foreground break-words">
                                                                {convertToEmojis(commentItem.comment)}
                                                            </MarkdownText>
                                                            {/* Show edited indicator */}
                                                            {isCommentEdited(commentItem) && (
                                                                <Tooltip>
                                                                    <TooltipTrigger asChild>
                                                                        <span className="text-[9px] text-muted-foreground italic ml-1">
                                                                            (edited)
                                                                        </span>
                                                                    </TooltipTrigger>
                                                                    <TooltipContent>
                                                                        Edited {new Date(commentItem.updated_at).toLocaleString()}
                                                                    </TooltipContent>
                                                                </Tooltip>
                                                            )}
                                                            {/* Inline spacer for timestamp */}
                                                            <span className="inline-block w-12"></span>
                                                        </div>
                                                    )}

                                                    {/* Timestamp - WhatsApp style bottom right */}
                                                    <div className="absolute bottom-0.5 right-1.5 flex items-center gap-0.5">
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <span className="text-[10px] text-muted-foreground/80 cursor-default">
                                                                    {getRelativeTime(commentItem.created_at)}
                                                                </span>
                                                            </TooltipTrigger>
                                                            <TooltipContent>
                                                                {new Date(commentItem.created_at).toLocaleString()}
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    </div>
                                                </div>

                                                {/* Action buttons - shows on hover */}
                                                <div className={cn(
                                                    "absolute top-1 right-1 flex items-center gap-0.5 z-10",
                                                    mobileScreen ? "opacity-100" : "opacity-0 group-hover:opacity-100 transition-opacity"
                                                )}>
                                                    {/* Reply button */}
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-6 w-6 bg-background/80 hover:bg-background text-muted-foreground hover:text-foreground"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleCommentReply(commentItem);
                                                                }}
                                                            >
                                                                <Reply className="h-3 w-3" />
                                                            </Button>
                                                        </TooltipTrigger>
                                                        <TooltipContent>Reply</TooltipContent>
                                                    </Tooltip>

                                                    {/* Edit button - only for own comments */}
                                                    {isOwnComment && (
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    className="h-6 w-6 bg-background/80 hover:bg-background text-muted-foreground hover:text-foreground"
                                                                    onClick={(e) => handleEditClick(commentItem, e)}
                                                                >
                                                                    <Pencil className="h-3 w-3" />
                                                                </Button>
                                                            </TooltipTrigger>
                                                            <TooltipContent>Edit comment</TooltipContent>
                                                        </Tooltip>
                                                    )}

                                                    {/* Archive button - only for own comments */}
                                                    {isOwnComment && (
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    className="h-6 w-6 bg-background/80 hover:bg-background text-muted-foreground hover:text-foreground"
                                                                    onClick={(e) => handleDeleteClick(commentItem, e)}
                                                                >
                                                                    <Trash2 className="h-3 w-3" />
                                                                </Button>
                                                            </TooltipTrigger>
                                                            <TooltipContent>Archive comment</TooltipContent>
                                                        </Tooltip>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </React.Fragment>
                            );
                        })}
                    </div>
                </div>

                {/* Multi-select toolbar */}
                {selectedComments.length > 0 && (
                    <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-50 bg-card border rounded-lg shadow-lg p-3 flex items-center gap-3">
                        <Badge variant="default" className="text-sm">
                            {selectedComments.length} selected
                        </Badge>
                        <Button
                            size="sm"
                            onClick={handlePromoteComments}
                            disabled={isPromoting || itemType !== 'folder'}
                            className="gap-2"
                        >
                            {isPromoting ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Promoting...
                                </>
                            ) : (
                                <>
                                    <ArrowUp className="h-4 w-4" />
                                    Promote to Folders
                                </>
                            )}
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={handleClearSelection}
                            className="gap-2"
                        >
                            <X className="h-4 w-4" />
                            Clear
                        </Button>
                    </div>
                )}

                {/* Comment input */}
                <div className={cn(
                    "shrink-0 border-t border-border bg-card/50 backdrop-blur-sm",
                    mobileScreen ? "p-2" : "p-3"
                )}>
                    {savingComment ? (
                        <div className="flex items-center justify-center gap-2 py-2 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin text-primary" />
                            <span className="animate-pulse">Saving comment...</span>
                        </div>
                    ) : (
                        <div className={cn(
                            "relative",
                            mobileScreen ? "w-full" : "w-full"
                        )}>
                            {/* Hidden file inputs */}
                            <input
                                type="file"
                                accept="image/*"
                                capture="environment"
                                onChange={handleFileChange}
                                ref={fileInputRef}
                                className="hidden"
                            />
                            <input
                                type="file"
                                accept="video/*"
                                capture="environment"
                                onChange={handleFileChange}
                                ref={videoFileInputRef}
                                className="hidden"
                            />
                            <input
                                type="file"
                                onChange={handleFileChange}
                                ref={generalFileInputRef}
                                className="hidden"
                            />

                            {/* Mention suggestions - positioned above the composer */}
                            {showMentionSuggestions && mentionSuggestions.length > 0 && (
                                <div
                                    ref={mentionSuggestionsRef}
                                    className={cn(
                                        "absolute bottom-full mb-2 bg-popover border shadow-lg max-h-[200px] overflow-y-auto z-50 rounded-lg",
                                        mobileScreen ? "left-2 right-2" : "left-3 right-3"
                                    )}
                                >
                                    {mentionSuggestions.map((user, index) => (
                                        <div
                                            key={user.id}
                                            className={cn(
                                                "flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors first:rounded-t-lg last:rounded-b-lg",
                                                index === selectedMentionIndex ? "bg-accent" : "hover:bg-muted/50"
                                            )}
                                            onClick={() => handleMentionSelect(user)}
                                        >
                                            <Avatar className="h-8 w-8 ring-2 ring-background">
                                                <AvatarImage src={user.picture} />
                                                <AvatarFallback className="text-xs font-medium bg-primary/10 text-primary">
                                                    {user.first_name?.[0] || user.username[0]}
                                                </AvatarFallback>
                                            </Avatar>
                                            <div className="flex flex-col min-w-0">
                                                <span className="text-sm font-medium truncate">{user.username}</span>
                                                <span className="text-xs text-muted-foreground truncate">
                                                    {user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.email}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Composer box - ChatGPT style */}
                            <div className={cn(
                                "flex flex-col bg-muted/50 rounded-3xl px-3 py-2",
                                "border border-border/50"
                            )}>
                                {/* Quoted Reply Preview (WhatsApp-style) */}
                                {selectedReplyComment && (
                                    <div className="mb-2 px-2 py-1.5 rounded border-l-3 bg-primary/20 border-primary/50 dark:bg-primary/10 flex items-start justify-between gap-2">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-1.5 mb-0.5">
                                                <Reply className="h-3 w-3 text-primary shrink-0" />
                                                <span className="text-[10px] font-semibold text-foreground/80">
                                                    Replying to {selectedReplyComment.user?.first_name} {selectedReplyComment.user?.last_name}
                                                </span>
                                            </div>
                                            <div className="text-[11px] text-foreground/70 line-clamp-1">
                                                {selectedReplyComment.media_type && (
                                                    <span className="italic">
                                                        {selectedReplyComment.media_type === 'image' && '📷 Image'}
                                                        {selectedReplyComment.media_type === 'video' && '🎥 Video'}
                                                        {selectedReplyComment.media_type === 'audio' && '🎤 Audio'}
                                                        {selectedReplyComment.media_type === 'other' && '📎 File'}
                                                        {selectedReplyComment.comment ? ': ' : ''}
                                                    </span>
                                                )}
                                                {selectedReplyComment.comment || ''}
                                            </div>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-5 w-5 shrink-0 text-muted-foreground hover:text-foreground"
                                            onClick={handleCancelReply}
                                        >
                                            <X className="h-3 w-3" />
                                        </Button>
                                    </div>
                                )}

                                {/* Textarea */}
                                <Textarea
                                    ref={inputRef}
                                    placeholder="Write a comment..."
                                    value={comment}
                                    spellCheck={false}
                                    onPaste={handlePaste}
                                    onKeyDown={(e) => {
                                        if (showMentionSuggestions && mentionSuggestions.length > 0) {
                                            if (e.key === 'ArrowDown') {
                                                e.preventDefault();
                                                setSelectedMentionIndex(prev =>
                                                    prev < mentionSuggestions.length - 1 ? prev + 1 : prev
                                                );
                                                return;
                                            } else if (e.key === 'ArrowUp') {
                                                e.preventDefault();
                                                setSelectedMentionIndex(prev => prev > 0 ? prev - 1 : prev);
                                                return;
                                            } else if (e.key === 'Enter' || e.key === 'Tab') {
                                                e.preventDefault();
                                                const selectedUser = mentionSuggestions[selectedMentionIndex];
                                                if (selectedUser) {
                                                    handleMentionSelect(selectedUser);
                                                }
                                                return;
                                            } else if (e.key === 'Escape') {
                                                e.preventDefault();
                                                setShowMentionSuggestions(false);
                                                setMentionQuery('');
                                                return;
                                            }
                                        }
                                        // For Enter key without mention suggestions, handle comment submission
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            handleCommentSubmit(e);
                                        } else if (e.key === 'Enter' && e.shiftKey) {
                                            // Allow Shift+Enter for new lines
                                            return;
                                        }
                                    }}
                                    onChange={(e) => {
                                        const newValue = e.target.value;
                                        setComment(newValue);

                                        // Check for @ mention
                                        const cursorPos = e.target.selectionStart || newValue.length;
                                        const textBeforeCursor = newValue.substring(0, cursorPos);
                                        const lastAtIndex = textBeforeCursor.lastIndexOf('@');

                                        if (lastAtIndex !== -1) {
                                            const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
                                            if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
                                                setMentionStartIndex(lastAtIndex);
                                                setMentionQuery(textAfterAt);
                                            } else {
                                                setShowMentionSuggestions(false);
                                                setMentionQuery('');
                                            }
                                        } else {
                                            setShowMentionSuggestions(false);
                                            setMentionQuery('');
                                        }
                                    }}
                                    rows={1}
                                    className={cn(
                                        "min-h-[36px] max-h-[200px] resize-none py-2 px-1",
                                        "bg-transparent border-0 rounded-none",
                                        "focus-visible:ring-0 focus-visible:ring-offset-0",
                                        "placeholder:text-muted-foreground/60 text-sm"
                                    )}
                                />

                                {/* Bottom toolbar */}
                                <div className="flex items-center justify-between pt-1 relative">
                                    {/* Left: Hide messages toggle + Attach/Menu button */}
                                    <div className="flex items-center gap-0.5">
                                        {/* Hide/Show messages toggle */}
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className={cn(
                                                        "h-8 w-8 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted",
                                                        focusModeMessagesHidden && "text-primary bg-primary/10"
                                                    )}
                                                    onClick={() => setFocusModeMessagesHidden(!focusModeMessagesHidden)}
                                                >
                                                    {focusModeMessagesHidden ? (
                                                        <Eye className="h-5 w-5" />
                                                    ) : (
                                                        <EyeOff className="h-5 w-5" />
                                                    )}
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent side="top">
                                                {focusModeMessagesHidden ? "Show all messages" : "Hide messages (show last only)"}
                                            </TooltipContent>
                                        </Tooltip>

                                        {mobileScreen ? (
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted"
                                                    >
                                                        <Paperclip className="h-5 w-5" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent
                                                    align="start"
                                                    side="top"
                                                    className="min-w-[160px] z-[1100]"
                                                >
                                                    <DropdownMenuItem onClick={handleUploadButtonClick} className="gap-2">
                                                        <FileUp className="h-4 w-4" />
                                                        Upload File
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        ) : (
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted"
                                                    >
                                                        <Paperclip className="h-5 w-5" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent
                                                    align="start"
                                                    side="top"
                                                    className="min-w-[160px] z-[1100]"
                                                >
                                                    <DropdownMenuItem onClick={handleUploadButtonClick} className="gap-2">
                                                        <FileUp className="h-4 w-4" />
                                                        Upload File
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        )}
                                    </div>

                                    {/* Center: Unread count + Read All button */}
                                    <div className="absolute left-1/2 transform -translate-x-1/2 flex items-center gap-2">
                                        <span className="text-xs text-muted-foreground">
                                            Unread count: <span className="font-semibold text-foreground">{ownUnreadCount}</span>
                                        </span>
                                        <Button
                                            size="sm"
                                            className="h-6 px-2 text-xs font-medium bg-blue-500 hover:bg-blue-600 text-white"
                                            onClick={() => markAllRead(itemId)}
                                            disabled={ownUnreadCount === 0}
                                        >
                                            Read All
                                        </Button>
                                    </div>

                                    {/* Right: Recording buttons + Send */}
                                    <div className="flex items-center gap-0.5">
                                        {/* Desktop: Recording buttons */}
                                        {!mobileScreen && (
                                            <div className="flex items-center text-muted-foreground [&_button]:h-8 [&_button]:w-8 [&_button]:rounded-full [&_button:hover]:text-foreground">
                                                <ScreenshotCapture onScreenshotComplete={handleScreenshotComplete} />
                                                <ScreenRecorder onRecordingComplete={handleScreenRecordingComplete} clearRecording={clearScreenRecordingRef} />
                                                <VideoRecorder onRecordingComplete={handleVideoRecordingComplete} clearRecording={clearVideoRecordingRef} />
                                                <AudioRecorder onRecordingComplete={handleRecordingComplete} clearRecording={clearRecordingRef} />
                                            </div>
                                        )}

                                        {/* Mobile: Audio recorder */}
                                        {mobileScreen && (
                                            <div className="text-muted-foreground [&_button]:h-8 [&_button]:w-8 [&_button]:rounded-full [&_button:hover]:text-foreground">
                                                <AudioRecorder onRecordingComplete={handleRecordingComplete} clearRecording={clearRecordingRef} />
                                            </div>
                                        )}

                                        {/* Send button */}
                                        <Button
                                            size="icon"
                                            className={cn(
                                                "h-8 w-8 rounded-full transition-colors",
                                                comment.trim()
                                                    ? "bg-primary text-primary-foreground hover:bg-primary/90"
                                                    : "bg-muted text-muted-foreground"
                                            )}
                                            onClick={() => {
                                                handleCommentSubmit({});
                                            }}
                                            disabled={!comment.trim() || savingComment}
                                        >
                                            {savingComment ? (
                                                <Clock className="h-5 w-5 animate-spin" />
                                            ) : (
                                                <Send className="h-5 w-5" />
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Media Preview Dialog */}
                <Dialog open={showRecordingDialog} onOpenChange={(open) => {
                    if (!open) {
                        // When dialog is closed via X button or clicking outside, cancel the recording
                        handleCancelRecording();
                    } else {
                        setShowRecordingDialog(open);
                    }
                }}>
                    <DialogContent className="max-w-md">
                        <DialogHeader>
                            <DialogTitle className="flex items-center justify-between">
                                <span>
                                    {mediaType === 'video' ? 'Video Ready' :
                                        mediaType === 'image' ? 'Image Ready' :
                                            mediaType === 'audio' ? 'Audio Ready' : 'Media Ready'}
                                </span>
                                {mediaFile && (
                                    <Badge variant="secondary">{formatFileSize(mediaFile.size)}</Badge>
                                )}
                            </DialogTitle>
                        </DialogHeader>

                        <div className="space-y-4">
                            {/* Audio Preview */}
                            {mediaFile && mediaType === 'audio' && (
                                <ReactAudioPlayer
                                    src={URL.createObjectURL(mediaFile)}
                                    controls
                                    className="w-full"
                                />
                            )}

                            {/* Video Preview */}
                            {mediaFile && mediaType === 'video' && (
                                <video
                                    controls
                                    preload="auto"
                                    className="w-full max-h-[400px] rounded-lg"
                                >
                                    <source src={URL.createObjectURL(mediaFile)} type={mediaFile.type} />
                                    Your browser does not support the video tag.
                                </video>
                            )}

                            {/* Image Preview */}
                            {mediaFile && mediaType === 'image' && (
                                <>
                                    {(() => {
                                        try {
                                            const imageUrl = URL.createObjectURL(mediaFile);
                                            return (
                                                <img
                                                    src={imageUrl}
                                                    alt="Preview"
                                                    className={cn(
                                                        "w-full max-h-[400px] object-contain rounded-lg",
                                                        mobileScreen && "border-4 border-muted"
                                                    )}
                                                    onError={(e) => {
                                                        console.error('Failed to load image preview:', mediaFile.name, mediaFile.type);
                                                        e.target.style.display = 'none';
                                                    }}
                                                    onLoad={(e) => {
                                                        // Ensure image is visible when loaded
                                                        e.target.style.display = 'block';
                                                    }}
                                                />
                                            );
                                        } catch (error) {
                                            console.error('Failed to create object URL for image:', error);
                                            return (
                                                <div className="p-4 text-center text-muted-foreground">
                                                    <p>Unable to preview image</p>
                                                    <p className="text-xs mt-1">{mediaFile.name}</p>
                                                </div>
                                            );
                                        }
                                    })()}

                                    {/* Screenshot Quality Selector */}
                                    {screenshotCanvas && (
                                        <div className="p-3 bg-muted rounded-lg space-y-3">
                                            <label className="text-sm font-semibold">JPEG Quality:</label>
                                            <TooltipProvider delayDuration={200}>
                                                <div className="flex gap-2">
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <Button
                                                                variant={screenshotQuality === '0.75' ? 'default' : 'outline'}
                                                                onClick={() => handleScreenshotQualityChange('0.75')}
                                                                className="px-4 py-2 h-10 font-medium"
                                                            >
                                                                75% (Smaller)
                                                            </Button>
                                                        </TooltipTrigger>
                                                        <TooltipContent side="bottom">
                                                            <p>Good balance between file size and quality</p>
                                                        </TooltipContent>
                                                    </Tooltip>
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <Button
                                                                variant={screenshotQuality === '1' ? 'default' : 'outline'}
                                                                onClick={() => handleScreenshotQualityChange('1')}
                                                                className="px-4 py-2 h-10 font-medium"
                                                            >
                                                                100% (Best)
                                                            </Button>
                                                        </TooltipTrigger>
                                                        <TooltipContent side="bottom">
                                                            <p>Maximum detail, larger file size</p>
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </div>
                                            </TooltipProvider>
                                        </div>
                                    )}
                                </>
                            )}

                            {/* Other File Types Preview (PDFs, docs, archives, etc.) */}
                            {mediaFile && mediaType === 'other' && (
                                <div className="flex items-center gap-3 p-4 rounded-lg border border-border bg-muted/50">
                                    <div className="w-12 h-12 rounded flex items-center justify-center bg-gray-500 shrink-0">
                                        <FileUp className="h-6 w-6 text-white" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium text-foreground truncate">
                                            {mediaFile.name}
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                            {formatFileSize(mediaFile.size)} • Ready to send
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Caption Input */}
                            <Textarea
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                placeholder="Add a caption (optional)"
                                rows={2}
                            />
                        </div>

                        <DialogFooter className="flex-row justify-between sm:justify-between">
                            <TooltipProvider delayDuration={200}>
                                <div className="flex gap-2">
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button
                                                variant="outline"
                                                size="icon"
                                                className="h-12 w-12 rounded-full border-2 border-destructive text-destructive"
                                                onClick={handleCancelRecording}
                                            >
                                                <X className="h-5 w-5" />
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent side="top">
                                            <p>Cancel</p>
                                        </TooltipContent>
                                    </Tooltip>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button
                                                variant="outline"
                                                size="icon"
                                                className="h-12 w-12 rounded-full border-2 border-primary text-primary"
                                                onClick={handleDownloadRecording}
                                            >
                                                <Download className="h-5 w-5" />
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent side="top">
                                            <p>Download</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </div>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            size="icon"
                                            className="h-12 w-12 rounded-full"
                                            onClick={handleSendRecording}
                                        >
                                            <Send className="h-5 w-5" />
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top">
                                        <p>Send</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Photo Annotation Dialog */}
                <Dialog open={showAnnotationDialog} onOpenChange={setShowAnnotationDialog}>
                    <DialogContent className={cn("max-w-3xl", mobileScreen && "h-screen max-h-screen p-0")}>
                        <DialogHeader className={cn("px-6 pt-6 pb-2", mobileScreen && "px-4 pt-4 pb-2")}>
                            <DialogTitle className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 rounded-lg bg-primary/10">
                                        <Brush className="h-5 w-5 text-primary" />
                                    </div>
                                    <div>
                                        <span className="text-lg font-semibold">Annotate Image</span>
                                        <p className="text-xs text-muted-foreground font-normal mt-0.5">
                                            Draw on the image to highlight areas
                                        </p>
                                    </div>
                                </div>
                                <TooltipProvider delayDuration={200}>
                                    <div className="flex items-center gap-1 p-1 rounded-lg bg-muted/50">
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button
                                                    variant={brushSize === '10' ? 'default' : 'ghost'}
                                                    size="icon"
                                                    className={cn(
                                                        "h-9 w-9 rounded-md",
                                                        brushSize === '10' && "bg-[#39FF14]/20 text-[#39FF14] hover:bg-[#39FF14]/30"
                                                    )}
                                                    onClick={() => setBrushSize('10')}
                                                >
                                                    <Circle className="h-3 w-3" fill="#39FF14" stroke="#39FF14" />
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent side="bottom">
                                                <p>Small brush</p>
                                            </TooltipContent>
                                        </Tooltip>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button
                                                    variant={brushSize === '30' ? 'default' : 'ghost'}
                                                    size="icon"
                                                    className={cn(
                                                        "h-9 w-9 rounded-md",
                                                        brushSize === '30' && "bg-[#39FF14]/20 text-[#39FF14] hover:bg-[#39FF14]/30"
                                                    )}
                                                    onClick={() => setBrushSize('30')}
                                                >
                                                    <Circle className="h-5 w-5" fill="#39FF14" stroke="#39FF14" />
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent side="bottom">
                                                <p>Large brush</p>
                                            </TooltipContent>
                                        </Tooltip>
                                    </div>
                                </TooltipProvider>
                            </DialogTitle>
                        </DialogHeader>

                        <div className={cn(
                            "px-6 py-2",
                            mobileScreen && "flex-1 h-[calc(100vh-150px)] px-0 py-0"
                        )}>
                            <canvas
                                ref={(el) => {
                                    annotationCanvasRef.current = el;
                                    if (el && annotationImage && !imageLoadedRef.current) {
                                        const img = new Image();
                                        img.onload = () => {
                                            el.width = img.width;
                                            el.height = img.height;
                                            const ctx = el.getContext('2d');
                                            ctx.drawImage(img, 0, 0);
                                            ctx.strokeStyle = '#39FF14';
                                            ctx.lineWidth = parseInt(brushSize);
                                            ctx.lineCap = 'round';
                                            ctx.lineJoin = 'round';
                                            imageLoadedRef.current = true;
                                        };
                                        img.src = annotationImage;
                                    }
                                }}
                                onMouseDown={(e) => {
                                    setIsDrawing(true);
                                    const canvas = annotationCanvasRef.current;
                                    const rect = canvas.getBoundingClientRect();
                                    const ctx = canvas.getContext('2d');
                                    ctx.strokeStyle = '#39FF14';
                                    ctx.lineWidth = parseInt(brushSize);
                                    ctx.lineCap = 'round';
                                    ctx.lineJoin = 'round';
                                    ctx.beginPath();
                                    ctx.moveTo((e.clientX - rect.left) * (canvas.width / rect.width), (e.clientY - rect.top) * (canvas.height / rect.height));
                                }}
                                onMouseMove={(e) => {
                                    if (!isDrawing) return;
                                    const canvas = annotationCanvasRef.current;
                                    const rect = canvas.getBoundingClientRect();
                                    const ctx = canvas.getContext('2d');
                                    ctx.lineTo((e.clientX - rect.left) * (canvas.width / rect.width), (e.clientY - rect.top) * (canvas.height / rect.height));
                                    ctx.stroke();
                                }}
                                onMouseUp={() => setIsDrawing(false)}
                                onMouseLeave={() => setIsDrawing(false)}
                                onTouchStart={(e) => {
                                    setIsDrawing(true);
                                    const canvas = annotationCanvasRef.current;
                                    const rect = canvas.getBoundingClientRect();
                                    const ctx = canvas.getContext('2d');
                                    const touch = e.touches[0];
                                    ctx.strokeStyle = '#39FF14';
                                    ctx.lineWidth = parseInt(brushSize);
                                    ctx.lineCap = 'round';
                                    ctx.lineJoin = 'round';
                                    ctx.beginPath();
                                    ctx.moveTo((touch.clientX - rect.left) * (canvas.width / rect.width), (touch.clientY - rect.top) * (canvas.height / rect.height));
                                }}
                                onTouchMove={(e) => {
                                    if (!isDrawing) return;
                                    e.preventDefault();
                                    const canvas = annotationCanvasRef.current;
                                    const rect = canvas.getBoundingClientRect();
                                    const ctx = canvas.getContext('2d');
                                    const touch = e.touches[0];
                                    ctx.lineTo((touch.clientX - rect.left) * (canvas.width / rect.width), (touch.clientY - rect.top) * (canvas.height / rect.height));
                                    ctx.stroke();
                                }}
                                onTouchEnd={() => setIsDrawing(false)}
                                className={cn(
                                    "w-full object-contain touch-none rounded-lg cursor-crosshair",
                                    mobileScreen ? "h-full" : "max-h-[60vh] border border-border shadow-sm"
                                )}
                            />
                        </div>

                        <DialogFooter className={cn(
                            "flex-row justify-between sm:justify-between gap-3 px-6 pb-6 pt-4",
                            mobileScreen && "px-4 pb-4"
                        )}>
                            <TooltipProvider delayDuration={200}>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            variant="outline"
                                            size="lg"
                                            className="gap-2"
                                            onClick={() => {
                                                // Skip annotation but still proceed to preview
                                                setShowAnnotationDialog(false);
                                                setAnnotationImage(null);
                                                imageLoadedRef.current = false;
                                                setBrushSize('10');
                                                // If we have a mediaFile, show the recording dialog
                                                if (mediaFile) {
                                                    setIsMediaSelected(true);
                                                    setMediaType('image');
                                                    // For screenshots, regenerate with default quality
                                                    if (screenshotCanvas) {
                                                        setScreenshotQuality('0.75');
                                                        generateScreenshotBlob(screenshotCanvas, 0.75);
                                                    } else {
                                                        setShowRecordingDialog(true);
                                                    }
                                                }
                                            }}
                                        >
                                            Skip Annotation
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top">
                                        <p>Continue without drawing</p>
                                    </TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            size="lg"
                                            className="gap-2"
                                            onClick={() => {
                                                const canvas = annotationCanvasRef.current;
                                                canvas.toBlob((blob) => {
                                                    const humanTimestamp = getHumanReadableTimestamp();
                                                    const annotatedFile = new File([blob], `annotated_photo_${humanTimestamp}.jpg`, {
                                                        type: 'image/jpeg',
                                                        lastModified: Date.now()
                                                    });
                                                    setMediaFile(annotatedFile);
                                                    setIsMediaSelected(true);
                                                    setMediaType('image');
                                                    setShowAnnotationDialog(false);
                                                    setAnnotationImage(null);
                                                    imageLoadedRef.current = false;
                                                    setBrushSize('10');
                                                    // Clear screenshotCanvas since we're using the annotated version
                                                    setScreenshotCanvas(null);
                                                    setShowRecordingDialog(true);
                                                }, 'image/jpeg', 0.8);
                                            }}
                                        >
                                            <Brush className="h-4 w-4" />
                                            Done
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top">
                                        <p>Save annotations and continue</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Edit Dialog for Desktop */}
                {!mobileScreen && (
                    <Dialog open={editingCommentId !== null} onOpenChange={(open) => !open && handleCancelEdit()}>
                        <DialogContent className="sm:max-w-[70vw]">
                            <DialogHeader>
                                <DialogTitle>Edit Comment</DialogTitle>
                            </DialogHeader>
                            <div className="relative">
                                {/* Mention suggestions */}
                                {showEditMentionSuggestions && editMentionSuggestions.length > 0 && (
                                    <div className="absolute bottom-full left-0 right-0 mb-1 bg-popover border rounded-lg shadow-lg max-h-[200px] overflow-y-auto z-50">
                                        {editMentionSuggestions.map((user, index) => (
                                            <div
                                                key={user.id}
                                                className={cn(
                                                    "flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors first:rounded-t-lg last:rounded-b-lg",
                                                    index === selectedEditMentionIndex ? "bg-accent" : "hover:bg-muted/50"
                                                )}
                                                onClick={() => handleEditMentionSelect(user)}
                                            >
                                                <Avatar className="h-8 w-8 ring-2 ring-background">
                                                    <AvatarImage src={user.picture} />
                                                    <AvatarFallback className="text-xs font-medium bg-primary/10 text-primary">
                                                        {user.first_name?.[0] || user.username[0]}
                                                    </AvatarFallback>
                                                </Avatar>
                                                <div className="flex flex-col min-w-0">
                                                    <span className="text-sm font-medium truncate">{user.username}</span>
                                                    <span className="text-xs text-muted-foreground truncate">
                                                        {user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.email}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <Textarea
                                    ref={editInputRef}
                                    value={editedComment}
                                    onChange={handleEditTextChange}
                                    onKeyDown={handleEditKeyDown}
                                    autoFocus
                                    rows={3}
                                    className="resize-none max-h-[280px] overflow-y-auto"
                                    disabled={isUpdating}
                                />
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={handleCancelEdit} disabled={isUpdating}>
                                    Cancel
                                </Button>
                                <Button onClick={handleSaveEdit} disabled={isUpdating}>
                                    {isUpdating ? 'Saving...' : 'Save'}
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                )}

                {/* Edit Sheet for Mobile */}
                {mobileScreen && (
                    <Sheet open={editingCommentId !== null} onOpenChange={(open) => !open && handleCancelEdit()}>
                        <SheetContent side="bottom" className="rounded-t-xl">
                            <SheetHeader>
                                <SheetTitle>Edit Comment</SheetTitle>
                            </SheetHeader>
                            <div className="px-4 py-2 relative">
                                {/* Mention suggestions */}
                                {showEditMentionSuggestions && editMentionSuggestions.length > 0 && (
                                    <div className="absolute bottom-full left-4 right-4 mb-1 bg-popover border rounded-lg shadow-lg max-h-[200px] overflow-y-auto z-50">
                                        {editMentionSuggestions.map((user, index) => (
                                            <div
                                                key={user.id}
                                                className={cn(
                                                    "flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors first:rounded-t-lg last:rounded-b-lg",
                                                    index === selectedEditMentionIndex ? "bg-accent" : "hover:bg-muted/50"
                                                )}
                                                onClick={() => handleEditMentionSelect(user)}
                                            >
                                                <Avatar className="h-8 w-8 ring-2 ring-background">
                                                    <AvatarImage src={user.picture} />
                                                    <AvatarFallback className="text-xs font-medium bg-primary/10 text-primary">
                                                        {user.first_name?.[0] || user.username[0]}
                                                    </AvatarFallback>
                                                </Avatar>
                                                <div className="flex flex-col min-w-0">
                                                    <span className="text-sm font-medium truncate">{user.username}</span>
                                                    <span className="text-xs text-muted-foreground truncate">
                                                        {user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.email}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <Textarea
                                    ref={editInputRef}
                                    value={editedComment}
                                    onChange={handleEditTextChange}
                                    onKeyDown={handleEditKeyDown}
                                    autoFocus
                                    rows={3}
                                    className="resize-none max-h-[280px] overflow-y-auto"
                                    disabled={isUpdating}
                                />
                            </div>
                            <SheetFooter className="flex-row gap-2">
                                <Button variant="outline" onClick={handleCancelEdit} disabled={isUpdating} className="flex-1">
                                    Cancel
                                </Button>
                                <Button onClick={handleSaveEdit} disabled={isUpdating} className="flex-1">
                                    {isUpdating ? 'Saving...' : 'Save'}
                                </Button>
                            </SheetFooter>
                        </SheetContent>
                    </Sheet>
                )}

                {/* Archive Confirmation Dialog */}
                <Dialog open={commentToDelete !== null} onOpenChange={(open) => !open && handleCancelDelete()}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Archive Comment</DialogTitle>
                        </DialogHeader>
                        <div className="py-4">
                            <p className="text-sm text-muted-foreground">
                                Are you sure you want to archive this comment? This action cannot be undone.
                            </p>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={handleCancelDelete} disabled={isDeleting}>
                                Cancel
                            </Button>
                            <Button
                                variant="destructive"
                                onClick={handleConfirmDelete}
                                disabled={isDeleting}
                            >
                                {isDeleting ? 'Archiving...' : 'Archive'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </TooltipProvider>
    );
};

export default FocusModeCommentsPanel;
